import {
  HsBox,
  HsGraphicCollection,
  HsIndexedGeometry,
  HsSceneMaterialCache,
  HsSceneMaterials,
  HsSceneObjects,
  HsOrderedBatches,
  HsStableRenderOrder,
  HsTextureMaterial,
  hsDownloadGeometryBatches,
  hsGroupInstancingCandidates,
  hsSplitInstancingBatches,
  type HsBatchTicket,
  type HsRenderPriorityEntry,
  type HsClippingStorage,
  type HsDisposable,
  type HsFrustumObjectInfo,
  type HsPerspectiveCamera,
  type HsViewerHandle,
  type HsViewerPlugin,
  type HsViewerPluginContext,
  type HsFrustumRequestInfo,
  type HsFrustumView,
  type HsGeometry,
  type HsGraphicLines,
  type HsGraphicObject,
  type HsGraphicPoints,
  type HsGraphicQueue,
  type HsGraphics,
  type HsMaterial,
  type HsSceneMeshUpsertResult,
  type HsSceneUpsertResult,
  type HsShaderData,
  type HsDecodedLine,
  type HsDecodedMesh,
  type HsDecodedPointCloud,
  type HsMaterialView,
  type HsNodeSmallView,
  type HsOriginalMeshSource,
  type HsSceneObjectsHooks,
  type HsVertexColorGroupView,
} from '@sibe/hyperscene';
import { HsDecodedGeometryStore, hsNormalizeGeometryId, type HsPointCloudLimits } from './decoded-geometry-store.class';
import { HsFrustumRefreshScheduler, type HsRefreshTimers } from './frustum-refresh-scheduler.class';
import { hsMakeFrustumRequest, type HsFrustumCutBox } from './frustum-request.function';
import {
  HsFrustumGeometrySet,
  HsFrustumSceneState,
  hsFrustumHasMeshReplacement,
  hsFrustumRenderKeyNodeId,
  hsNormalizeOptionalId,
  type HsFrustumDelta,
} from './frustum-scene-state.class';
import { HsNodeVisibility, type HsNodeVisibilityState } from './node-visibility.class';
import {
  hsPlanFrustumDownloads,
  type HsFrustumDownloadKind,
  type HsFrustumDownloadPlan,
  type HsFrustumDownloadSettings,
} from './frustum-download-plan.function';
import {
  hsCorrectOriginalMesh,
  HsMeshMaterialValidationError,
  HsSharedMeshMaterialSplits,
  type HsCachedMeshMaterialParts,
  type HsMeshMaterialPartInfo,
  type HsSharedMeshMaterialSplitResult,
} from './mesh-material-parts.function';
import {
  hsDecodeCompressedMeshes,
  hsDecodeCompressedPointClouds,
  hsIsCompressedGeometryPayload,
} from '@sibe/hyperscene/formats/draco';
import { HsGeometryBinaryDecoder } from '@sibe/hyperscene/formats/geometry';
import {
  hsInterleavePointCloudOrder,
  type HsMutableNumericArray,
} from '../../../shared/streaming/point-cloud-interleave.function';
import {
  HS_GEOMETRY_DECODE_CANCELED,
  HsGeometryDecodePool,
  type HsGeometryDecodePoolOptions,
} from './geometry-decode-pool.class';
import {
  HsGeometryPayloadCache,
  hsMakeGeometryPayloadKey,
  type HsGeometryPayloadCacheOptions,
  type HsGeometryPayloadKind,
} from './geometry-payload-cache.class';

/** Largest node batch the original mesh sources endpoint accepts. */
const ORIGINAL_SOURCE_REQUEST_MAX_SIZE: number = 1024;
/** Largest texture batch the documents endpoint accepts. */
const TEXTURE_REQUEST_MAX_SIZE: number = 100;
const DEFAULT_MATERIAL_RGBA: readonly number[] = [0.76, 0.76, 0.76, 1];

/** Version of the geometry wire format; part of every payload cache key. */
const GEOMETRY_PROTOCOL_VERSION: number = 2;

/** Download options of one geometry payload request. */
export interface HsGeometryPayloadRequest {
  signal?: AbortSignal;
  /** Payload format (`binary`, `draco`). */
  format?: string;
  /** Meshes whose canonical (original) vertex order the server must keep. */
  canonicalGeometryIds?: readonly string[];
}

/** How geometry is downloaded and decoded; hosts may change it at any time. */
export interface HsSceneLoadSettings {
  /** Decode in workers; off decodes on the main thread. */
  useWorkers: boolean;
  workerPoolSize: number;
  /** Keep downloads in IndexedDB. */
  useCache: boolean;
  /** `read-through` reads the cache before downloading; `write-through` only writes it. */
  cacheMode: 'read-through' | 'write-through';
}

/** Download and decode counters, for diagnostics panels. */
export interface HsSceneLoadStats {
  cacheHits: number;
  cacheMisses: number;
  workerStatus: string;
  decodeLastMode: string;
  decodeLastMs: number;
  decodeAvgMs: number;
  decodeSamples: number;
  decodeInflight: number;
  decodeQueuePeak: number;
  decodeFallbacks: number;
  decodeCanceled: number;
}

/** Timing hooks; `HsViewerProfiler` fits them. A null span is not ended. */
export interface HsSceneSourceSpans {
  startSpan(name: string): unknown;
  endSpan(span: never): void;
}

/** The decoded content of one set of payloads. */
export interface HsDecodedPayloads {
  meshes: HsDecodedMesh[];
  lines: HsDecodedLine[];
  pointClouds: HsDecodedPointCloud[];
}

/** True for the error a canceled decode or an aborted download rejects with. */
export function hsIsDecodeCanceled(error: unknown): boolean {
  if (error instanceof Error && error.message === HS_GEOMETRY_DECODE_CANCELED) {
    return true;
  }
  return error instanceof DOMException && error.name === 'AbortError';
}

/** Objects drawn and meshes rejected by one `applyGeometry` call. */
export interface HsSceneApplyStats {
  meshCount: number;
  lineCount: number;
  pointCloudCount: number;
  collidedPointsCount: number;
  meshRejectedInvalidVertices: number;
  meshRejectedEmptyIndices: number;
  meshRejectedInvalidIndices: number;
  meshRejectedIndexedBuildFailed: number;
  meshRejectedRegularFallbackFailed: number;
}

/** Counters with nothing drawn. */
export function hsEmptySceneApplyStats(): HsSceneApplyStats {
  return {
    meshCount: 0,
    lineCount: 0,
    pointCloudCount: 0,
    collidedPointsCount: 0,
    meshRejectedInvalidVertices: 0,
    meshRejectedEmptyIndices: 0,
    meshRejectedInvalidIndices: 0,
    meshRejectedIndexedBuildFailed: 0,
    meshRejectedRegularFallbackFailed: 0,
  };
}

/** How the scene looks; a change of `signature` rebuilds every object and material. */
export interface HsSceneLook {
  /**
   * Changes whenever the scene's shaders change: opacity, transparency, the number of clipping planes, collision
   * highlighting or shading. Moved clipping planes keep it, so the drawn objects stay and take the new planes as
   * uniforms.
   */
  signature: string;
  /** Scene opacity (1 when transparent materials are off). */
  opacity: number;
  disableTransparentMaterials: boolean;
  clippingStorage: HsClippingStorage | null;
  highlightCollisions: boolean;
  /** Share geometry between instances and batch them. */
  instancing: boolean;
  /** Build indexed geometry ready for instanced drawing. */
  instancingGeometry: boolean;
  pointSize: number;
  configureMeshShader(shaderData: HsShaderData): HsShaderData;
}

/** Where `applyGeometry` draws. */
export interface HsSceneTarget {
  gl: WebGL2RenderingContext;
  graphics: HsGraphics;
  queue: HsGraphicQueue;
}

export interface HsSceneApplyOptions {
  /** Render keys that stay drawn; others are pruned (default: the keys of the given geometry). */
  expectedMeshGeometryIds?: readonly string[];
  expectedLineGeometryIds?: readonly string[];
  expectedPointCloudGeometryIds?: readonly string[];
  /** Pruned objects only leave the queue and keep their geometry (visibility changes). */
  preserveGeometryForVisibility?: boolean;
  /** Batch interchangeable meshes into instanced collections (default: the look's `instancing`). */
  batchInstancedGeometry?: boolean;
  skipStaleObjectPruning?: boolean;
}

/** The outcome of `applyGeometry`: counters, the drawn bounds and the materials used. */
export interface HsSceneApplyResult {
  stats: HsSceneApplyStats;
  bounds: HsBox;
  materials: HsSceneMaterials;
}

/** How `refresh` draws: which host extras the call needs besides the scene objects. */
export type HsSceneRefreshStage =
  /** A chunk of a large delta; the host defers its own Frustum culling refresh. */
  | 'chunk'
  /** Prunes objects that left a visibility-only view; nothing new is drawn. */
  | 'visibility'
  /** Progress after a downloaded batch; host overlays (measurements, markers) are redrawn. */
  | 'progress'
  /** The last draw of a refresh; the host restarts distributed rendering. */
  | 'final';

export interface HsSceneRefreshApplyOptions extends HsSceneApplyOptions {
  stage: HsSceneRefreshStage;
}

/** Download and draw sizes `refresh` reads when it starts. */
export interface HsSceneRefreshSettings extends HsFrustumDownloadSettings {
  /** Objects per `apply` call while a large delta is drawn. */
  renderBatchSize: number;
  /** Main thread time (ms) a refresh draws before yielding a frame. */
  renderBudgetMs: number;
  /** Decoded point clouds kept: points and geometries. */
  pointCloudLimits: HsPointCloudLimits;
  /** Batch interchangeable meshes when the refresh completes. */
  instancing: boolean;
}

/** Progress of one refresh: geometry batches (or geometries for adaptive batches) completed of the total. */
export interface HsSceneRefreshProgress {
  completed: number;
  total: number;
  message: string;
  /** Counters of everything drawn so far. */
  stats: HsSceneApplyStats;
  bytes: HsSceneRefreshBytes;
}

export interface HsSceneRefreshBytes {
  meshes: number;
  lines: number;
  pointClouds: number;
}

/** What a refresh needs from the viewer that hosts the scene. */
export interface HsSceneRefreshHost {
  /** The Frustum request for the current camera under the given session. */
  frustumRequest(sessionId: string): HsFrustumRequestInfo;
  /** Draws decoded geometry, usually through `applyGeometry`, and returns its counters. */
  apply(
    meshes: readonly HsDecodedMesh[],
    lines: readonly HsDecodedLine[],
    pointClouds: readonly HsDecodedPointCloud[],
    options: HsSceneRefreshApplyOptions
  ): HsSceneApplyStats;
  /** Resolves on the next frame; long work yields to the browser through it. */
  nextFrame(): Promise<void>;
  settings: HsSceneRefreshSettings;
  /** Changes with everything that alters how the scene looks; an unchanged view is not redrawn. */
  lookSignature: string;
  /** Aborts the downloads of this refresh. */
  signal: AbortSignal;
  /** Scene tree roots whose original materials composite instances use. */
  rootNodeIds: readonly string[];
  /** Prune objects that left the view but keep their geometry (visibility toggles). */
  visibilityOnly?: boolean;
  /** True once a newer refresh waits; downloads still running are dropped. */
  superseded?(): boolean;
  /** Called before a delta that replaces meshes (an LOD change) is drawn. */
  onMeshReplacement?(): void;
  /** Called when a texture batch of drawn meshes finished loading. */
  onTexturesLoaded?(): void;
  onProgress?(progress: HsSceneRefreshProgress): void;
}

/** The outcome of a refresh that drew the new view. */
export interface HsSceneRefreshResult {
  stats: HsSceneApplyStats;
  bytes: HsSceneRefreshBytes;
  /** Counts of the current view. */
  geometryIds: { meshes: number; lines: number; pointClouds: number };
  decoded: { meshes: number; lines: number; pointClouds: number };
}

/** Request, download and draw settings of a scene source that runs its own refreshes. */
export interface HsSceneStreamingSettings extends HsSceneRefreshSettings {
  /** What each Frustum request asks for, per object kind. */
  frustumObjects: HsFrustumObjectInfo[];
  /** Limits every request to nodes with one of these tags. */
  nodeTags: string[];
  /**
   * Limits every request to nodes whose bounds meet this world box (the Frustum `cutArea`), such as a section box:
   * geometry the section hides is not downloaded. Absent or null downloads the whole view.
   */
  cutBox?: HsFrustumCutBox | null;
  /** Quiet time after the camera stops before the view refreshes, in ms. */
  refreshDelayMs: number;
}

/** The demo viewer's defaults; tune per device and dataset. */
export const HS_DEFAULT_SCENE_STREAMING: HsSceneStreamingSettings = {
  frustumObjects: [
    { type: 'mesh', limit: 150_000, angle: 0.5, sortOrder: 'angular-size' },
    { type: 'line', limit: 150_000, angle: 1, sortOrder: 'distance' },
    {
      type: 'pointCloud',
      limit: 8_000_000,
      lodStrategy: 'budget',
      targetSpacingPixels: 2,
      minSpacingPixels: 1,
      boxesLimit: 100_000,
      sortOrder: 'angular-size',
    },
  ],
  nodeTags: [],
  refreshDelayMs: 150,
  downloadBatchSize: 500,
  texturedMeshDownloadBatchSize: 50,
  pointCloudDownloadBatchSize: 100,
  renderBatchSize: 500,
  renderBudgetMs: 40,
  pointCloudLimits: { points: 8_000_000, geometries: 100_000 },
  instancing: true,
};

/** Opaque scene, no clipping, unmodified shaders. */
export const HS_DEFAULT_SCENE_LOOK: HsSceneLook = {
  signature: 'default',
  opacity: 1,
  disableTransparentMaterials: false,
  clippingStorage: null,
  highlightCollisions: false,
  instancing: true,
  instancingGeometry: true,
  pointSize: 2,
  configureMeshShader: (shaderData: HsShaderData): HsShaderData => shaderData,
};

/** What a scene source needs from the viewer it is installed into; `HsHypersceneViewer` provides it. */
export interface HsSceneViewer extends HsViewerHandle {
  readonly gl: WebGL2RenderingContext;
  readonly camera: HsPerspectiveCamera;
  readonly surface: { readonly graphics: HsGraphics; readonly objectsQueue: HsGraphicQueue };
  readonly render: { clearMeshBatches(): void };
  /** Re-runs culling on the next frame. */
  invalidate(): void;
  /** The visible object under a client point; needed by `HsSceneSource.pick`. */
  pick?(clientX: number, clientY: number): HsViewerPickResult | null;
}

/** A pick result as `HsHypersceneViewer.pick` returns it. */
export interface HsViewerPickResult {
  object: HsGraphicObject;
  instanceIndex: number | null;
  point: [number, number, number];
}

/** The scene node under a client point. */
export interface HsScenePick {
  nodeId: string;
  /** Render key of the hit instance (`<node>|<geometry>|...`). */
  renderKey: string;
  /** World position of the hit. */
  point: [number, number, number];
}

/** Events of a scene source that runs its own refreshes (`open`, `update`, camera moves). */
export interface HsSceneSourceEvents {
  progress: HsSceneRefreshProgress;
  /** A refresh drew the new view. */
  refreshed: HsSceneRefreshResult;
  /** A refresh failed; the next camera move or `update` retries. */
  error: unknown;
  /** Geometry was drawn: at each stage of a refresh, after textures arrived and on `reapply`. */
  applied: HsSceneAppliedEvent;
  /** A refresh started (`update` or a camera stop). */
  started: HsSceneUpdateOptions;
  /** A refresh ended: with its result, or null when it failed, changed nothing or was dropped. */
  finished: HsSceneRefreshResult | null;
}

/** What a refresh drew and at which stage. */
export interface HsSceneAppliedEvent {
  stats: HsSceneApplyStats;
  /**
   * `chunk` while batches stream in, `progress` after each download round, `final` at the end, `textures` after a
   * texture batch and `reapply` after a look change.
   */
  stage: HsSceneRefreshStage | 'textures' | 'reapply';
}

/** How `update` refreshes. */
export interface HsSceneUpdateOptions {
  /** Only scene visibility changed: keep decoded geometry the view still lists and skip unchanged downloads. */
  visibilityOnly?: boolean;
}

/** How `open` loads a scene. */
export interface HsSceneOpenOptions {
  /** Root nodes of the scene when the host already knows them; the source then skips the root request. */
  rootNodeIds?: readonly string[];
}

/** The endpoints the scene source reads; `HsViewerApi` provides them. */
export interface HsSceneSourceApi {
  getGeometryPayload(
    kind: HsGeometryPayloadKind,
    geometryIds: readonly string[],
    request: HsGeometryPayloadRequest
  ): Promise<ArrayBuffer>;
  getMaterials(materialIds: readonly string[]): Promise<HsMaterialView[]>;
  getNodeTreeOriginalMaterials(rootNodeId: string): Promise<HsMaterialView[]>;
  getSceneRootNodes(sceneId: string): Promise<HsNodeSmallView[]>;
  getDocuments(documentIds: readonly string[]): Promise<Map<string, Blob>>;
  getDocument(documentId: string): Promise<Blob>;
  /** Instances the camera needs, as a delta against the request's session. */
  getSceneFrustum(sceneId: string, request: HsFrustumRequestInfo, signal?: AbortSignal): Promise<HsFrustumView>;
  /** Original meshes that composite instances of these nodes replace, with their correction matrices. */
  getOriginalMeshSources(nodeIds: readonly string[]): Promise<HsOriginalMeshSource[]>;
}

export interface HsSceneSourceOptions {
  api: HsSceneSourceApi;
  /** The viewer's WebGL context, or null before it exists (default: the installing viewer's). */
  gl?: () => WebGL2RenderingContext | null;
  /** Increases when the open scene changes; work for an older epoch is dropped (default: `open`/`close`). */
  currentEpoch?: () => number;
  /** Texture memory kept for textures the view no longer uses, in bytes. */
  textureBudgetBytes?: number;
  /** Textures per download request. */
  textureBatchSize?: () => number;
  /** Decoded mesh memory kept for meshes the view no longer uses, in bytes. */
  meshCacheBudgetBytes?: number;
  /** Geometry ids per payload request. */
  downloadBatchSize?: () => number;
  /** Called when textures or decoded meshes were added or evicted (for debug counters). */
  onCacheChanged?: () => void;
  /** Payload formats the viewer requests per kind. */
  geometryFormats?: () => { meshes: string; pointClouds: string };
  /** Scene and model the payload cache files downloads under. */
  cacheScope?: () => { sceneId: string; modelId: string };
  /** Timing hooks, or null while profiling is off. */
  spans?: () => HsSceneSourceSpans | null;
  /** Reports problems the source recovered from, such as an unavailable cache. */
  onWarning?: (message: string) => void;
  /** Diagnostic events (`geometry.apply.start`, `geometry.material-error`, ...). */
  onEvent?: (name: string, detail: string) => void;
  /** Called when a look change removed every scene object; the viewer drops batches and culling caches. */
  onSceneCleared?: () => void;
  /** Called with a summary when composite materials of some meshes could not be applied. */
  onMaterialError?: (summary: string) => void;
  /** Creates Frustum session ids (default random UUIDs); recordings and tests pass fixed ones. */
  newSessionId?: () => string;
  /** Timer functions for the Frustum refresh debounce; hosts pass their window's. */
  timers?: HsRefreshTimers;
  /** What the viewer updates when scene objects change (default: re-cull the installing viewer). */
  objectHooks?: HsSceneObjectsHooks;
  /** IndexedDB cache of mesh and line downloads (point clouds are never written to it). */
  payloadCache?: HsGeometryPayloadCacheOptions;
  /**
   * Workers that decode geometry payloads; without them payloads are decoded on the main thread. The pool
   * follows the source's epoch (`currentEpoch` plus `open` and `close`); a `currentEpoch` given here is ignored.
   */
  decodePool?: Omit<HsGeometryDecodePoolOptions, 'currentEpoch'> & Partial<HsGeometryDecodePoolOptions>;
  /**
   * What `open` and `update` request and how they download and draw (default `HS_DEFAULT_SCENE_STREAMING`). A
   * function is read at every refresh, for hosts whose settings change while a scene is open.
   */
  streaming?: Partial<HsSceneStreamingSettings> | (() => Partial<HsSceneStreamingSettings>);
  /** Refresh by itself when the camera stops (default true); hosts that schedule refreshes call `update`. */
  followCamera?: boolean;
  /** False while refreshes must wait (frozen view, active navigation); scheduled refreshes start once true. */
  canRefresh?: () => boolean;
  /** Waits for the next frame between draw batches (default the next animation frame). */
  nextFrame?: () => Promise<void>;
  /** How `open` and `update` draw the scene (default `HS_DEFAULT_SCENE_LOOK`); call `reapply` after it changes. */
  look?: () => HsSceneLook;
  /** Plugin name, unique within a viewer (default `scene`). */
  name?: string;
}

/** `HsSceneSourceOptions` with the defaults filled in. */
interface HsResolvedSceneSourceOptions {
  api: HsSceneSourceApi;
  gl: () => WebGL2RenderingContext | null;
  currentEpoch: () => number;
  textureBudgetBytes: number;
  textureBatchSize: () => number;
  meshCacheBudgetBytes: number;
  downloadBatchSize: () => number;
  onCacheChanged?: () => void;
  geometryFormats: () => { meshes: string; pointClouds: string };
  cacheScope: () => { sceneId: string; modelId: string };
  spans?: () => HsSceneSourceSpans | null;
  onWarning?: (message: string) => void;
  onEvent?: (name: string, detail: string) => void;
  onSceneCleared?: () => void;
  onMaterialError?: (summary: string) => void;
  newSessionId?: () => string;
  timers: HsRefreshTimers;
  objectHooks: HsSceneObjectsHooks;
  payloadCache: HsGeometryPayloadCacheOptions;
  decodePool: HsGeometryDecodePoolOptions;
  look?: () => HsSceneLook;
  streaming?: Partial<HsSceneStreamingSettings> | (() => Partial<HsSceneStreamingSettings>);
  followCamera?: boolean;
  canRefresh?: () => boolean;
  nextFrame?: () => Promise<void>;
}

/**
 * Streams a Hyperscene scene into a viewer over Frustum: which instances the camera needs, their geometry
 * downloads (cached and decoded in workers), and the GPU objects and materials that draw them.
 *
 * Installed into a viewer (`viewer.use(source)`), it refreshes the view when the camera stops: `open` a scene,
 * listen with `on`, `close` it. Hosts with their own drawing and loading UI call `refresh` with their own host.
 */
export class HsSceneSource implements HsViewerPlugin {
  public readonly name: string;
  /** Instances the current Frustum session reported, per geometry kind. */
  public readonly frustum: HsFrustumSceneState = new HsFrustumSceneState();
  public readonly scheduler: HsFrustumRefreshScheduler;
  /** GPU objects of the scene by render key. */
  public readonly objects: HsSceneObjects;
  /** Stable draw order of the queue: scene objects by geometry and render key, then other objects. */
  public readonly renderOrder: HsStableRenderOrder<HsGraphicObject> = new HsStableRenderOrder();
  public readonly materials: HsSceneMaterialCache = new HsSceneMaterialCache();
  /** Decoded geometry kept for re-rendering without downloading again. */
  public readonly geometry: HsDecodedGeometryStore = new HsDecodedGeometryStore();
  public readonly payloadCache: HsGeometryPayloadCache;
  public readonly decodePool: HsGeometryDecodePool;
  /** Material colors (RGBA, 0..1) by material id. */
  public materialRgbaById: Record<string, number[]> = {};
  public textureIdByMaterialId: Record<string, string> = {};
  /** Per-vertex color groups of composite materials. */
  public vertexGroupsById: Record<string, HsVertexColorGroupView[]> = {};
  /** Uploaded textures by document id, with their estimated GPU bytes. */
  public readonly textures: Map<string, WebGLTexture> = new Map();
  public readonly textureBytes: Map<string, number> = new Map();
  /** Textures the last full render used; eviction keeps them. */
  public renderedTextureIds: Set<string> = new Set();
  public readonly loadSettings: HsSceneLoadSettings = {
    useWorkers: true,
    workerPoolSize: 2,
    useCache: false,
    cacheMode: 'read-through',
  };
  public readonly stats: HsSceneLoadStats = {
    cacheHits: 0,
    cacheMisses: 0,
    workerStatus: 'disabled',
    decodeLastMode: 'none',
    decodeLastMs: 0,
    decodeAvgMs: 0,
    decodeSamples: 0,
    decodeInflight: 0,
    decodeQueuePeak: 0,
    decodeFallbacks: 0,
    decodeCanceled: 0,
  };
  /** Validation errors of composite (vertex color) materials by render key. */
  public readonly materialErrors: Map<string, string> = new Map();
  /** Material parts of composite meshes: part render key to its parent and color, and per parent the split. */
  public readonly meshPartInfoByRenderKey: Map<string, HsMeshMaterialPartInfo> = new Map();
  public readonly meshPartsByRenderKey: Map<string, HsCachedMeshMaterialParts> = new Map();
  /** Part render keys whose objects must be rebuilt because their parent's split changed. */
  public readonly meshPartRebuildKeys: Set<string> = new Set();
  public readonly sharedMaterialSplits: HsSharedMeshMaterialSplits = new HsSharedMeshMaterialSplits();
  /** Frustum session of the open scene; null starts a new one (the server then sends the full view). */
  public sessionId: string | null = null;
  /**
   * Viewer session for node visibility, sent with every Frustum request once set: nodes hidden through the session
   * endpoints of `HsViewerApi` with this id are left out for this viewer only. Null (the default) applies the scene's
   * visibility alone and spares the server the session lookup. It belongs to the viewer, not to a scene (the server
   * keeps the session's rules per scene), and outlives `sessionId`, which restarts with the Frustum cache; set it with
   * `startViewSession`, or to an id of your own, for example one kept in browser storage.
   */
  public viewSessionId: string | null = null;
  /** Decoded geometry of the current view under its render keys, as last drawn. */
  public drawnMeshes: HsDecodedMesh[] = [];
  public drawnLines: HsDecodedLine[] = [];
  public drawnPointClouds: HsDecodedPointCloud[] = [];
  /** Counters of the last refresh. */
  public renderedStats: HsSceneApplyStats = hsEmptySceneApplyStats();
  /** `viewSignature` of the last completed refresh. */
  public lastViewSignature: string | null = null;
  /** The scene `open` loaded, or null. */
  public sceneId: string | null = null;
  private readonly binaryDecoder: HsGeometryBinaryDecoder = new HsGeometryBinaryDecoder();
  private lastLookSignature: string | null = null;
  /** The clipping storage the drawn scene shaders read, kept while the look signature stays. */
  private drawnClippingStorage: HsClippingStorage | null = null;
  private readonly objectHooks: HsSceneObjectsHooks;
  private readonly options: HsResolvedSceneSourceOptions;
  private readonly listeners: { [K in keyof HsSceneSourceEvents]: Set<(value: HsSceneSourceEvents[K]) => void> } = {
    progress: new Set(),
    refreshed: new Set(),
    error: new Set(),
    applied: new Set(),
    started: new Set(),
    finished: new Set(),
  };
  private viewer: HsSceneViewer | null = null;
  private readonly nodeVisibility: HsNodeVisibility = new HsNodeVisibility();
  private epoch: number = 0;
  private rootNodeIds: string[] = [];
  private abortController: AbortController | null = null;
  private readonly lastCameraMatrix: Float64Array = new Float64Array(16).fill(Number.NaN);

  public constructor(options: HsSceneSourceOptions) {
    // `open` and `close` cancel work as well as a host epoch does; the sum grows whenever either grows.
    const hostEpoch: (() => number) | undefined = options.currentEpoch;
    const currentEpoch: () => number =
      hostEpoch === undefined ? (): number => this.epoch : (): number => hostEpoch() + this.epoch;
    this.options = {
      ...options,
      gl: options.gl ?? ((): WebGL2RenderingContext | null => this.viewer?.gl ?? null),
      currentEpoch,
      textureBudgetBytes: options.textureBudgetBytes ?? 512 * 1024 * 1024,
      textureBatchSize: options.textureBatchSize ?? ((): number => this.streaming.texturedMeshDownloadBatchSize),
      meshCacheBudgetBytes: options.meshCacheBudgetBytes ?? 1024 * 1024 * 1024,
      downloadBatchSize: options.downloadBatchSize ?? ((): number => this.streaming.downloadBatchSize),
      geometryFormats:
        options.geometryFormats ??
        ((): { meshes: string; pointClouds: string } => ({
          meshes: 'binary',
          pointClouds: 'binary',
        })),
      cacheScope:
        options.cacheScope ??
        ((): { sceneId: string; modelId: string } => ({
          sceneId: this.sceneId ?? 'scene',
          modelId: 'scene',
        })),
      timers: options.timers ?? {
        setTimeout: (callback: () => void, delayMs: number): number =>
          globalThis.setTimeout(callback, delayMs) as unknown as number,
        clearTimeout: (handle: number): void => globalThis.clearTimeout(handle),
      },
      objectHooks: options.objectHooks ?? {
        invalidateTotals: (): void => undefined,
        refreshCulling: (): void => this.viewer?.invalidate(),
        invalidateOrder: (): void => {
          this.renderOrder.invalidate();
          this.viewer?.invalidate();
        },
      },
      payloadCache: options.payloadCache ?? { databaseName: 'hyperscene-geometry' },
      decodePool:
        options.decodePool !== undefined
          ? // The pool drops work of older epochs: it must count `open` and `close` as well as the host's epoch.
            { ...options.decodePool, currentEpoch }
          : {
              createWorker: (): Worker => {
                throw new Error('No decode workers were configured.');
              },
              currentEpoch,
            },
    };
    this.name = options.name ?? 'scene';
    if (options.decodePool === undefined) {
      this.loadSettings.useWorkers = false;
    }
    this.scheduler = new HsFrustumRefreshScheduler(this.options.timers);
    this.objectHooks = this.options.objectHooks;
    this.objects = new HsSceneObjects(this.objectHooks);
    this.payloadCache = new HsGeometryPayloadCache(this.options.payloadCache);
    this.decodePool = new HsGeometryDecodePool(this.options.decodePool);
  }

  /** Texture memory kept for textures the view no longer uses, in bytes. */
  public get textureBudgetBytes(): number {
    return this.options.textureBudgetBytes;
  }

  /** What the next refresh requests and how it downloads and draws. */
  public get streaming(): HsSceneStreamingSettings {
    const streaming: Partial<HsSceneStreamingSettings> | (() => Partial<HsSceneStreamingSettings>) | undefined =
      this.options.streaming;
    return { ...HS_DEFAULT_SCENE_STREAMING, ...(typeof streaming === 'function' ? streaming() : streaming) };
  }

  /** Starts refreshing the view of `context.viewer` whenever its camera stops moving. */
  public install(context: HsViewerPluginContext): HsDisposable {
    const viewer: HsSceneViewer | undefined = context.viewer as HsSceneViewer | undefined;
    if (viewer?.surface === undefined || viewer.gl === undefined) {
      throw new Error('HsSceneSource draws into a viewer: install it with viewer.use(source).');
    }
    if (this.viewer !== null) {
      throw new Error('HsSceneSource is already installed into a viewer.');
    }
    this.viewer = viewer;
    const frames: HsDisposable | null =
      this.options.followCamera === false ? null : viewer.onFrame(() => this.followCamera(viewer));
    return {
      dispose: (): void => {
        frames?.dispose();
        this.close();
        this.releaseGpuResources();
        this.viewer = null;
      },
    };
  }

  /** Calls `listener` on every `event` until the returned handle is disposed. */
  public on<K extends keyof HsSceneSourceEvents>(
    event: K,
    listener: (value: HsSceneSourceEvents[K]) => void
  ): HsDisposable {
    const listeners: Set<(value: HsSceneSourceEvents[K]) => void> = this.listeners[event];
    listeners.add(listener);
    return { dispose: (): void => void listeners.delete(listener) };
  }

  /**
   * Closes the open scene and loads `sceneId` for the installing viewer's camera. Resolves with the first
   * refresh, or null when another `open` or `close` came first.
   */
  public async open(sceneId: string, options: HsSceneOpenOptions = {}): Promise<HsSceneRefreshResult | null> {
    if (this.viewer === null) {
      throw new Error('Install HsSceneSource into a viewer (viewer.use(source)) before opening a scene.');
    }
    this.close();
    const epoch: number = this.epoch;
    const rootNodeIds: readonly string[] =
      options.rootNodeIds ??
      (await this.options.api.getSceneRootNodes(sceneId)).map((root: HsNodeSmallView) => root.id);
    if (epoch !== this.epoch) {
      return null;
    }
    // Camera moves start refreshes only from here on, so the first refresh is this one.
    this.sceneId = sceneId;
    this.rootNodeIds = [...rootNodeIds];
    return this.update();
  }

  /** Changes the root nodes of the open scene (models attached or removed); the next refresh requests them. */
  public setRootNodeIds(rootNodeIds: readonly string[]): void {
    this.rootNodeIds = [...rootNodeIds];
  }

  /** Stops the downloads of the running refresh; it ends without drawing more. */
  public abort(): void {
    this.abortController?.abort();
  }

  /**
   * Draws the current view again with the current `look`, without asking Frustum or downloading: for look
   * changes such as opacity or clipping. Returns null before a viewer is installed.
   */
  public reapply(): HsSceneApplyStats | null {
    const viewer: HsSceneViewer | null = this.viewer;
    if (viewer === null) {
      return null;
    }
    const look: HsSceneLook = this.options.look?.() ?? HS_DEFAULT_SCENE_LOOK;
    const stats: HsSceneApplyStats = this.applyGeometry(
      this.drawnMeshes,
      this.drawnLines,
      this.drawnPointClouds,
      { gl: viewer.gl, graphics: viewer.surface.graphics, queue: viewer.surface.objectsQueue },
      look,
      {
        expectedMeshGeometryIds: this.frustum.meshes.renderKeys,
        expectedLineGeometryIds: this.frustum.lines.renderKeys,
        expectedPointCloudGeometryIds: this.frustum.pointClouds.renderKeys,
        batchInstancedGeometry: this.streaming.instancing,
      }
    ).stats;
    // The next refresh compares against what is drawn now.
    this.lastViewSignature = this.viewSignature(look.signature);
    viewer.invalidate();
    const event: HsSceneAppliedEvent = { stats, stage: 'reapply' };
    this.listeners.applied.forEach((listener: (value: HsSceneAppliedEvent) => void) => listener(event));
    return stats;
  }

  /**
   * Refreshes the open scene for the current camera now. A call while a refresh runs makes that refresh
   * stop downloading and run again once it ends. Failures go to `error` listeners.
   */
  public async update(options: HsSceneUpdateOptions = {}): Promise<HsSceneRefreshResult | null> {
    const viewer: HsSceneViewer | null = this.viewer;
    const sceneId: string | null = this.sceneId;
    if (viewer === null || sceneId === null) {
      return null;
    }
    if (this.scheduler.inProgress) {
      this.scheduler.pending = true;
      return null;
    }
    this.scheduler.begin();
    // Includes the host's epoch: a refresh for a scene the host switched away from reports nothing.
    const sceneEpoch: number = this.options.currentEpoch();
    this.listeners.started.forEach((listener: (value: HsSceneUpdateOptions) => void) => listener(options));
    let finished: HsSceneRefreshResult | null = null;
    try {
      const result: HsSceneRefreshResult | null = await this.refresh(
        sceneId,
        this.viewerHost(viewer, options.visibilityOnly === true)
      );
      if (result !== null) {
        this.listeners.refreshed.forEach((listener: (value: HsSceneRefreshResult) => void) => listener(result));
      }
      finished = result;
      return result;
    } catch (error: unknown) {
      if (sceneEpoch === this.options.currentEpoch() && !hsIsDecodeCanceled(error)) {
        this.listeners.error.forEach((listener: (value: unknown) => void) => listener(error));
      }
      return null;
    } finally {
      if (sceneEpoch === this.options.currentEpoch()) {
        this.listeners.finished.forEach((listener: (value: HsSceneRefreshResult | null) => void) => listener(finished));
      }
      // A host that switched scenes reset the scheduler for its next refresh, which this one must not end.
      if (sceneEpoch === this.options.currentEpoch() && this.scheduler.finish()) {
        this.scheduleUpdate();
      }
    }
  }

  /**
   * Hides the nodes' geometry on screen at once, without asking the server: pass every node of a subtree to
   * hide the subtree. Objects keep their geometry, so `showNodes` brings them back without downloads, and
   * refreshes keep them hidden. Persisting the change is the host's job; `restoreNodeVisibility` undoes it.
   */
  public hideNodes(nodeIds: Iterable<string>): void {
    this.nodeVisibility.hide(nodeIds);
    this.applyNodeVisibility();
  }

  /** Shows nodes `hideNodes` or `isolateNodes` hid; geometry the view never loaded comes with the next refresh. */
  public showNodes(nodeIds: Iterable<string>): void {
    this.nodeVisibility.show(nodeIds);
    this.applyNodeVisibility();
  }

  /** Hides every node but `nodeIds` on screen at once, like `hideNodes`. */
  public isolateNodes(nodeIds: Iterable<string>): void {
    this.nodeVisibility.isolate(nodeIds);
    this.applyNodeVisibility();
  }

  /** Shows every node the viewer hid on its own. */
  public showAllNodes(): void {
    this.nodeVisibility.showAll();
    this.applyNodeVisibility();
  }

  /**
   * Starts a viewer session for per-viewer visibility, unless one is running, and returns its id for the session
   * endpoints of `HsViewerApi`. The next refresh asks for this viewer's visibility.
   */
  public startViewSession(): string {
    this.viewSessionId ??= (this.options.newSessionId ?? newSessionId)();
    return this.viewSessionId;
  }

  /** Whether `hideNodes` or `isolateNodes` hides the node's geometry now. */
  public isNodeHidden(nodeId: string): boolean {
    return this.nodeVisibility.isHidden(nodeId);
  }

  /** Grows with every visibility change, so a host can tell whether another change came after its own. */
  public get nodeVisibilityRevision(): number {
    return this.nodeVisibility.revision;
  }

  /** The nodes hidden on screen now, to restore with `restoreNodeVisibility`. */
  public nodeVisibilityState(): HsNodeVisibilityState {
    return this.nodeVisibility.snapshot();
  }

  /** Brings back a `nodeVisibilityState`, for example after the server rejected a change. */
  public restoreNodeVisibility(state: HsNodeVisibilityState): void {
    this.nodeVisibility.restore(state);
    this.applyNodeVisibility();
  }

  /**
   * The scene node drawn at a client (CSS pixel) point of the installing viewer, or null when nothing of
   * the scene is there.
   */
  public pick(clientX: number, clientY: number): HsScenePick | null {
    const hit: HsViewerPickResult | null = this.viewer?.pick?.(clientX, clientY) ?? null;
    const renderKey: string | null = hit === null ? null : this.renderKeyOf(hit.object, hit.instanceIndex);
    const nodeId: string | null = renderKey === null ? null : hsNodeIdOfRenderKey(renderKey);
    return hit === null || renderKey === null || nodeId === null ? null : { nodeId, renderKey, point: hit.point };
  }

  /**
   * The queue's objects in a draw order that stays stable across frames: scene objects keyed by their
   * geometry and render key, then the queue's other objects (overlays, photogrammetry) in queue order.
   */
  public drawOrder(queue: HsGraphicQueue): readonly HsGraphicObject[] {
    return this.renderOrder.get(
      queue.objects.size,
      (cachedEntries: ReadonlyMap<HsGraphicObject, HsRenderPriorityEntry<HsGraphicObject>>) => {
        const entries: HsRenderPriorityEntry<HsGraphicObject>[] = [];
        const appendEntry = (object: HsGraphicObject, geometryKey: string, objectKey: string, radius: number): void => {
          const cached: HsRenderPriorityEntry<HsGraphicObject> | undefined = cachedEntries.get(object);
          entries.push(
            cached !== undefined &&
              cached.geometryKey === geometryKey &&
              cached.objectKey === objectKey &&
              Object.is(cached.radius, radius)
              ? cached
              : { object, geometryKey, objectKey, radius }
          );
        };
        const tracked: Set<HsGraphicObject> = new Set();
        const groups: {
          kind: string;
          objects: ReadonlyMap<string, HsGraphicObject>;
          geometryIds: Record<string, string>;
        }[] = [
          { kind: 'mesh', objects: this.objects.meshes, geometryIds: this.frustum.meshes.geometryIdByRenderKey },
          { kind: 'line', objects: this.objects.lines, geometryIds: this.frustum.lines.geometryIdByRenderKey },
          {
            kind: 'pointCloud',
            objects: this.objects.pointClouds,
            geometryIds: this.frustum.pointClouds.geometryIdByRenderKey,
          },
        ];
        for (const group of groups) {
          for (const [renderKey, object] of group.objects) {
            if (queue.objects.get(object.uuid) !== object || tracked.has(object)) {
              continue;
            }
            appendEntry(
              object,
              `${group.kind}:${group.geometryIds[renderKey] ?? renderKey}`,
              renderKey,
              object.boundingSphere.radius
            );
            tracked.add(object);
          }
        }
        for (const object of queue.objects.values()) {
          if (!tracked.has(object)) {
            appendEntry(object, `transient:${object.uuid}`, `${object.name}:${object.uuid}`, 0);
          }
        }
        return entries;
      }
    );
  }

  /**
   * The render key a scene object draws, or null for objects that are not the scene's. Instanced batches
   * need the hit `instanceIndex`.
   */
  public renderKeyOf(object: HsGraphicObject, instanceIndex: number | null = null): string | null {
    if (object instanceof HsGraphicCollection) {
      return instanceIndex === null ? null : this.objects.renderKeysByCollection.get(object)?.[instanceIndex] ?? null;
    }
    const groups: ReadonlyMap<string, HsGraphicObject>[] = [
      this.objects.meshes,
      this.objects.lines,
      this.objects.pointClouds,
    ];
    for (const objects of groups) {
      for (const [renderKey, sceneObject] of objects) {
        if (sceneObject === object) {
          return renderKey;
        }
      }
    }
    return null;
  }

  /**
   * Removes the open scene from the viewer and forgets its streaming state. Uploaded textures and the
   * IndexedDB payload cache stay for the next scene.
   */
  public close(): void {
    this.epoch++;
    this.abortController?.abort();
    this.abortController = null;
    this.scheduler.reset();
    const viewer: HsSceneViewer | null = this.viewer;
    if (viewer !== null) {
      this.clearObjects(viewer.surface.objectsQueue, viewer.surface.graphics);
      viewer.render.clearMeshBatches();
      viewer.invalidate();
    }
    this.nodeVisibility.showAll();
    this.frustum.clear();
    this.geometry.clear();
    this.clearCompositeSources();
    this.materialRgbaById = {};
    this.textureIdByMaterialId = {};
    this.vertexGroupsById = {};
    this.sessionId = null;
    this.drawnMeshes = [];
    this.drawnLines = [];
    this.drawnPointClouds = [];
    this.renderedStats = hsEmptySceneApplyStats();
    this.lastViewSignature = null;
    this.sceneId = null;
    this.rootNodeIds = [];
  }

  public dispose(): void {
    this.close();
    this.scheduler.reset();
    this.decodePool.dispose();
    this.releaseGpuResources();
  }

  /**
   * Asks Frustum which instances the camera needs and brings the scene to them: drops what left the view,
   * draws what is already decoded, then downloads, decodes and draws the rest in batches while textures
   * load. Returns null when the view is unchanged or the refresh was dropped for a newer scene or request;
   * throws when a download, a decode or a texture fails.
   */
  // eslint-disable-next-line complexity
  public async refresh(sceneId: string, host: HsSceneRefreshHost): Promise<HsSceneRefreshResult | null> {
    const epoch: number = this.options.currentEpoch();
    const current = (): boolean => epoch === this.options.currentEpoch();
    const live = (): boolean => current() && !host.signal.aborted && host.superseded?.() !== true;
    const visibilityOnly: boolean = host.visibilityOnly === true;
    const { meshes, lines, pointClouds } = this.frustum;
    if (this.sessionId === null) {
      this.sessionId = (this.options.newSessionId ?? newSessionId)();
    }
    let deltaReceived: boolean = false;
    try {
      const bytes: HsSceneRefreshBytes = { meshes: 0, lines: 0, pointClouds: 0 };
      const request: HsFrustumRequestInfo = host.frustumRequest(this.sessionId);
      const view: HsFrustumView = await this.timed('frustum.request', () =>
        this.options.api.getSceneFrustum(sceneId, request, host.signal)
      );
      if (!current()) {
        return null;
      }
      deltaReceived = true;
      const replacement: boolean = hsFrustumHasMeshReplacement(view);
      if (replacement) {
        host.onMeshReplacement?.();
      }
      const delta: HsFrustumDelta = this.frustum.applyFrustum(view, request.sceneSessionId);
      const missingLines: string[] = this.missingGeometryIds('lines', lines.geometryIds);
      const missingPointClouds: string[] = this.missingGeometryIds('pointClouds', pointClouds.geometryIds);
      const meshKeys: Set<string> = new Set(meshes.renderKeys);
      for (const key of this.materialErrors.keys()) {
        if (!meshKeys.has(key)) {
          this.materialErrors.delete(key);
        }
      }
      if (this.geometry.prunePointClouds(host.settings.pointCloudLimits, new Set(pointClouds.geometryIds))) {
        this.options.onCacheChanged?.();
      }
      await this.refreshMissingMaterials(sceneId, host.rootNodeIds, false);
      await this.refreshCompositeSources();
      const missingMeshes: string[] = this.missingMeshGeometryIds(meshes.geometryIds);
      if (!current()) {
        return null;
      }
      this.drawnMeshes = this.collectMeshes(meshes.renderKeys);
      this.drawnLines = this.collectLines(lines.renderKeys);
      this.drawnPointClouds = this.collectPointClouds(pointClouds.renderKeys);
      const added = (set: HsFrustumGeometrySet, previous: ReadonlySet<string>): string[] =>
        set.renderKeys.filter((renderKey: string) => !previous.has(renderKey));
      const meshesToApply: HsDecodedMesh[] = visibilityOnly
        ? this.collectMeshes(added(meshes, delta.previousRenderKeys.mesh))
        : this.drawnMeshes;
      const linesToApply: HsDecodedLine[] = visibilityOnly
        ? this.collectLines(added(lines, delta.previousRenderKeys.line))
        : this.drawnLines;
      const pointCloudsToApply: HsDecodedPointCloud[] = visibilityOnly
        ? this.collectPointClouds(added(pointClouds, delta.previousRenderKeys.pointcloud))
        : this.drawnPointClouds;
      const signature: string = this.viewSignature(host.lookSignature);
      if (
        !delta.newSession &&
        !replacement &&
        missingMeshes.length + missingLines.length + missingPointClouds.length === 0 &&
        this.lastViewSignature === signature &&
        Object.keys(this.materialRgbaById).length > 0
      ) {
        return null;
      }

      const expected: HsSceneApplyOptions = {
        expectedMeshGeometryIds: meshes.renderKeys,
        expectedLineGeometryIds: lines.renderKeys,
        expectedPointCloudGeometryIds: pointClouds.renderKeys,
      };
      const stats: HsSceneApplyStats = hsEmptySceneApplyStats();
      const appliedCount: number = meshesToApply.length + linesToApply.length + pointCloudsToApply.length;
      if (appliedCount > 0) {
        addStats(
          stats,
          await this.applyInChunks(host, meshesToApply, linesToApply, pointCloudsToApply, visibilityOnly, replacement)
        );
      } else if (visibilityOnly) {
        host.apply([], [], [], {
          ...expected,
          stage: 'visibility',
          preserveGeometryForVisibility: true,
          batchInstancedGeometry: false,
        });
      }
      let skipPruning: boolean = replacement || appliedCount > 0 || visibilityOnly;
      let progress: Pick<HsSceneRefreshProgress, 'completed' | 'total' | 'message'> = {
        completed: 0,
        total: 1,
        message: 'Preparing visible scene geometry...',
      };
      const report = (): void => host.onProgress?.({ ...progress, stats: this.renderedStats, bytes: { ...bytes } });
      const publish = (): void => {
        host.apply([], [], [], {
          ...expected,
          stage: 'progress',
          preserveGeometryForVisibility: visibilityOnly,
          batchInstancedGeometry: false,
          skipStaleObjectPruning: skipPruning,
        });
        skipPruning = true;
        this.renderedStats = {
          ...stats,
          meshCount: this.objects.meshes.size,
          lineCount: this.objects.lines.size,
          pointCloudCount: this.objects.pointClouds.size,
        };
        report();
      };
      publish();
      await host.nextFrame();

      const plan: HsFrustumDownloadPlan = hsPlanFrustumDownloads(
        this.frustum,
        { meshes: missingMeshes, lines: missingLines, pointClouds: missingPointClouds },
        this.textureIdByMaterialId,
        host.settings
      );
      const progressTotal: number = 1 + (plan.adaptive ? plan.geometryCount : plan.batchCount);
      let completedBatches: number = 0;
      let completedIds: number = 0;
      progress = {
        completed: 1,
        total: progressTotal,
        message: plan.batchCount > 0 ? 'Downloading visible scene geometry...' : 'Applying visible scene geometry...',
      };
      report();
      const textures: Promise<{ error: unknown } | null> = this.loadTextures(this.textureIds(), () => {
        if (current() && !host.signal.aborted) {
          host.onTexturesLoaded?.();
        }
      }).then(
        () => null,
        (error: unknown) => ({ error })
      );
      const ordered: HsOrderedBatches = new HsOrderedBatches();
      const completed: boolean = await hsDownloadGeometryBatches<HsFrustumDownloadKind>(
        plan.groups,
        host.settings.downloadBatchSize,
        async (kind: HsFrustumDownloadKind, geometryIds: string[]): Promise<number> => {
          const batch: HsBatchTicket = ordered.begin();
          try {
            const payload: ArrayBuffer = await this.timed(`geometry.download.${kind}`, () =>
              this.downloadPayload(kind, geometryIds, host.signal)
            );
            await batch.previous;
            if (ordered.canceled(batch) || !live()) {
              throw new Error(HS_GEOMETRY_DECODE_CANCELED);
            }
            bytes[kind] += payload.byteLength;
            const empty: ArrayBuffer = new ArrayBuffer(0);
            const decoded: HsDecodedPayloads = await this.decodePayloads(
              kind === 'meshes' ? payload : empty,
              kind === 'lines' ? payload : empty,
              kind === 'pointClouds' ? payload : empty
            );
            if (!live()) {
              throw new Error(HS_GEOMETRY_DECODE_CANCELED);
            }
            decoded.meshes.forEach((mesh: HsDecodedMesh) => this.geometry.setMesh(mesh));
            decoded.lines.forEach((line: HsDecodedLine) => this.geometry.setLine(line));
            decoded.pointClouds.forEach((pointCloud: HsDecodedPointCloud) => this.geometry.setPointCloud(pointCloud));
            this.options.onEvent?.(
              'geometry.batch.decoded',
              `kind=${kind};requested=${geometryIds.length};meshes=${decoded.meshes.length};` +
                `lines=${decoded.lines.length};pointClouds=${decoded.pointClouds.length};bytes=${payload.byteLength}`
            );
            const batchIds: Set<string> = new Set(geometryIds.map((id: string) => hsNormalizeGeometryId(id)));
            const inBatch = (set: HsFrustumGeometrySet): string[] =>
              set.renderKeys.filter((renderKey: string) => batchIds.has(set.geometryIdByRenderKey[renderKey]));
            const batchMeshes: HsDecodedMesh[] = kind === 'meshes' ? this.collectMeshes(inBatch(meshes)) : [];
            const batchLines: HsDecodedLine[] = kind === 'lines' ? this.collectLines(inBatch(lines)) : [];
            const batchPointClouds: HsDecodedPointCloud[] =
              kind === 'pointClouds' ? this.collectPointClouds(inBatch(pointClouds)) : [];
            this.drawnMeshes = this.drawnMeshes.concat(batchMeshes);
            this.drawnLines = this.drawnLines.concat(batchLines);
            this.drawnPointClouds = this.drawnPointClouds.concat(batchPointClouds);
            addStats(
              stats,
              await this.timed('geometry.apply', () =>
                this.applyInChunks(host, batchMeshes, batchLines, batchPointClouds, false, replacement)
              )
            );
            this.trimMeshCache();
            this.options.onEvent?.(
              'geometry.batch.applied',
              `kind=${kind};requested=${geometryIds.length};meshes=${batchMeshes.length};` +
                `lines=${batchLines.length};pointClouds=${batchPointClouds.length}`
            );
            completedBatches++;
            completedIds += geometryIds.length;
            progress = {
              completed: 1 + (plan.adaptive ? completedIds : completedBatches),
              total: progressTotal,
              message: plan.adaptive
                ? `Downloaded ${completedIds} of ${plan.geometryCount} geometries.`
                : `Downloaded ${completedBatches} of ${plan.batchCount} geometry batches.`,
            };
            publish();
            return payload.byteLength;
          } catch (error: unknown) {
            ordered.fail(batch);
            throw error;
          } finally {
            batch.finish();
          }
        },
        live,
        () => host.nextFrame(),
        'pointClouds',
        ['meshes', 'pointClouds']
      );
      if (!completed || !current() || host.signal.aborted) {
        return null;
      }
      const textureFailure: { error: unknown } | null = await textures;
      if (textureFailure !== null) {
        const reason: string =
          textureFailure.error instanceof Error ? textureFailure.error.message : String(textureFailure.error);
        this.options.onWarning?.(`Some scene textures could not be loaded: ${reason}`);
      }
      host.apply([], [], [], {
        ...expected,
        stage: 'final',
        preserveGeometryForVisibility: visibilityOnly,
        batchInstancedGeometry: host.settings.instancing,
        skipStaleObjectPruning: !replacement,
      });
      this.renderedTextureIds = new Set(this.textureIds());
      this.trimMeshCache();
      this.trimTextures();
      if (visibilityOnly) {
        this.renderedStats = {
          ...this.renderedStats,
          meshCount: this.objects.meshes.size,
          lineCount: this.objects.lines.size,
          pointCloudCount: this.objects.pointClouds.size,
        };
      }
      this.lastViewSignature = signature;
      return {
        stats: this.renderedStats,
        bytes,
        geometryIds: {
          meshes: meshes.geometryIds.length,
          lines: lines.geometryIds.length,
          pointClouds: pointClouds.geometryIds.length,
        },
        decoded: {
          meshes: this.drawnMeshes.length,
          lines: this.drawnLines.length,
          pointClouds: this.drawnPointClouds.length,
        },
      };
    } catch (error: unknown) {
      // A canceled refresh keeps the session: its delta is applied and the next refresh downloads what is missing.
      if (current() && deltaReceived && !hsIsDecodeCanceled(error)) {
        this.sessionId = null;
      }
      throw error;
    }
  }

  /**
   * Signature of the drawn view: the render keys and placements of every instance plus the host's look.
   * An unchanged signature with nothing to download means a refresh has nothing to redraw.
   */
  public viewSignature(lookSignature: string): string {
    const placements = (set: HsFrustumGeometrySet): string =>
      set.renderKeys
        .map((renderKey: string) => {
          const matrix: number[] | undefined = set.worldMatricesByRenderKey[renderKey];
          return Array.isArray(matrix) && matrix.length === 16
            ? `${renderKey}:${matrix.join(',')}`
            : `${renderKey}:identity`;
        })
        .join('|');
    const { meshes, lines, pointClouds } = this.frustum;
    return [
      `m:${meshes.renderKeys.join(',')}`,
      `l:${lines.renderKeys.join(',')}`,
      `p:${pointClouds.renderKeys.join(',')}`,
      `mm:${placements(meshes)}`,
      `lm:${placements(lines)}`,
      `pm:${placements(pointClouds)}`,
      lookSignature,
    ].join('||');
  }

  /**
   * Draws decoded geometry: splits composite meshes into material parts, prunes objects whose render keys
   * left the view, keeps or creates the objects of the given meshes, lines and point clouds and batches
   * interchangeable meshes. A new look rebuilds everything.
   */
  // eslint-disable-next-line complexity
  public applyGeometry(
    meshes: readonly HsDecodedMesh[],
    lines: readonly HsDecodedLine[],
    pointClouds: readonly HsDecodedPointCloud[],
    target: HsSceneTarget,
    look: HsSceneLook,
    options: HsSceneApplyOptions = {}
  ): HsSceneApplyResult {
    const { gl, graphics, queue } = target;
    const frustum: HsFrustumSceneState = this.frustum;
    const batchInstanced: boolean = options.batchInstancedGeometry ?? look.instancing;
    this.options.onEvent?.(
      'geometry.apply.start',
      `meshes=${meshes.length};lines=${lines.length};pointClouds=${pointClouds.length};` +
        `instancing=${look.instancing && batchInstanced};` +
        `skipPrune=${options.skipStaleObjectPruning === true}`
    );
    if (this.lastLookSignature !== look.signature) {
      this.clearObjects(queue, graphics);
      this.options.onSceneCleared?.();
      this.materials.clear(gl);
      this.lastLookSignature = look.signature;
      this.drawnClippingStorage = look.clippingStorage;
    } else if (
      look.clippingStorage !== null &&
      this.drawnClippingStorage !== null &&
      look.clippingStorage !== this.drawnClippingStorage
    ) {
      // A look built with a new storage for moved planes: the drawn shaders take the planes as uniforms.
      copyClippingPlanes(look.clippingStorage, this.drawnClippingStorage);
    }
    const clippingStorage: HsClippingStorage | null = this.drawnClippingStorage ?? look.clippingStorage;
    const materialMeshes: HsDecodedMesh[] = this.expandMaterialParts(meshes, look.disableTransparentMaterials);
    const referenceCounts: Map<HsGeometry, number> | undefined =
      options.skipStaleObjectPruning !== true || this.meshPartRebuildKeys.size > 0
        ? this.objects.countGeometryReferences()
        : undefined;
    for (const key of this.meshPartRebuildKeys) {
      const object: HsGraphicObject | undefined = this.objects.meshes.get(key);
      if (object !== undefined) {
        this.objects.dispose(queue, graphics, object, referenceCounts);
        this.objects.meshes.delete(key);
      }
    }
    this.meshPartRebuildKeys.clear();

    const spans: HsSceneSourceSpans | null = this.options.spans?.() ?? null;
    if (options.skipStaleObjectPruning !== true) {
      const pruneSpan: unknown = spans?.startSpan('geometry.apply.pruning') ?? null;
      const keysOf = (
        expected: readonly string[] | undefined,
        geometry: readonly { geometryId?: string }[],
        fallback: readonly string[]
      ): Set<string> =>
        new Set(
          (
            expected ??
            geometry.map((item: { geometryId?: string }, index: number) =>
              hsNormalizeGeometryId(item.geometryId ?? fallback[index] ?? '')
            )
          ).filter((id: string) => id.length > 0)
        );
      this.objects.prune(
        new Set(
          this.expandMaterialPartKeys(
            [...keysOf(options.expectedMeshGeometryIds, meshes, frustum.meshes.renderKeys)],
            look.disableTransparentMaterials
          )
        ),
        this.objects.meshes,
        queue,
        graphics,
        options.preserveGeometryForVisibility ?? false,
        referenceCounts
      );
      this.objects.prune(
        keysOf(options.expectedLineGeometryIds, lines, frustum.lines.renderKeys),
        this.objects.lines,
        queue,
        graphics,
        options.preserveGeometryForVisibility ?? false,
        referenceCounts
      );
      const activeMeshKeys: Set<string> = new Set(
        options.expectedMeshGeometryIds ?? meshes.map((mesh: HsDecodedMesh) => mesh.geometryId)
      );
      for (const key of this.meshPartsByRenderKey.keys()) {
        if (!activeMeshKeys.has(key)) {
          for (const part of this.meshPartsByRenderKey.get(key)?.parts ?? []) {
            this.meshPartInfoByRenderKey.delete(part.mesh.geometryId);
          }
          this.meshPartsByRenderKey.delete(key);
        }
      }
      this.objects.prune(
        keysOf(options.expectedPointCloudGeometryIds, pointClouds, frustum.pointClouds.renderKeys),
        this.objects.pointClouds,
        queue,
        graphics,
        options.preserveGeometryForVisibility ?? false,
        referenceCounts
      );
      if (pruneSpan !== null) {
        spans?.endSpan(pruneSpan as never);
      }
    }

    const materials: HsSceneMaterials = new HsSceneMaterials({
      gl,
      cache: this.materials,
      cachePrefix: `scene:${look.signature}|opacity:${look.opacity}`,
      opacity: look.opacity,
      disableTransparentMaterials: look.disableTransparentMaterials,
      clippingStorage,
      configureMeshShader: (shaderData: HsShaderData) => look.configureMeshShader(shaderData),
    });
    const rgbaOf = (renderKey: string, materialIds: Record<string, string>): number[] | null => {
      if (materialIds === frustum.meshes.materialIdByRenderKey) {
        const part: HsMeshMaterialPartInfo | undefined = this.meshPartInfoByRenderKey.get(renderKey);
        if (part !== undefined) {
          return part.rgba;
        }
      }
      const materialId: string | undefined = materialIds[renderKey];
      if (materialId === undefined || materialId.length === 0) {
        return null;
      }
      return this.materialRgbaById[materialId] ?? null;
    };
    const meshMaterial = (renderKey: string): HsMaterial => {
      const rgba: number[] | null = rgbaOf(renderKey, frustum.meshes.materialIdByRenderKey);
      const parentKey: string = this.meshPartInfoByRenderKey.get(renderKey)?.parentRenderKey ?? renderKey;
      const materialId: string | undefined = frustum.meshes.materialIdByRenderKey[parentKey];
      const textureId: string | undefined =
        frustum.meshes.textureIdByRenderKey[parentKey] ??
        (materialId !== undefined ? this.textureIdByMaterialId[materialId] : undefined);
      const texture: WebGLTexture | undefined = textureId !== undefined ? this.textures.get(textureId) : undefined;
      return materials.meshMaterial(rgba, textureId, texture);
    };

    const bounds: HsBox = HsBox.makeEmpty();
    const stats: HsSceneApplyStats = hsEmptySceneApplyStats();

    const meshesSpan: unknown = spans?.startSpan('geometry.apply.meshes') ?? null;
    materialMeshes.forEach((mesh: HsDecodedMesh, index: number) => {
      const renderKey: string = hsNormalizeGeometryId(
        mesh.geometryId ?? frustum.meshes.renderKeys[index] ?? `mesh:${index}`
      );
      const part: HsMeshMaterialPartInfo | undefined = this.meshPartInfoByRenderKey.get(renderKey);
      const parentKey: string = part?.parentRenderKey ?? renderKey;
      const upserted: HsSceneMeshUpsertResult = this.objects.upsertMesh(
        renderKey,
        mesh,
        index,
        meshMaterial(renderKey),
        {
          sourceGeometryId: frustum.meshes.geometryIdByRenderKey[renderKey] ?? renderKey,
          worldMatrix: frustum.meshes.worldMatricesByRenderKey[parentKey],
          instanceWorldMatrix: frustum.meshes.worldMatricesByRenderKey[renderKey],
          shareSourceGeometry: look.instancing && part === undefined,
          instancingGeometry: look.instancingGeometry,
        },
        queue,
        graphics
      );
      if (upserted.indexedBuildFailed) {
        stats.meshRejectedIndexedBuildFailed++;
      }
      if (upserted.rejection === 'invalidVertices') {
        stats.meshRejectedInvalidVertices++;
      } else if (upserted.rejection === 'emptyIndices') {
        stats.meshRejectedEmptyIndices++;
      } else if (upserted.rejection === 'invalidIndices') {
        stats.meshRejectedInvalidIndices++;
      } else if (upserted.rejection === 'regularFallbackFailed') {
        stats.meshRejectedRegularFallbackFailed++;
      }
      if (upserted.object === null) {
        return;
      }
      if (upserted.kind !== 'collection') {
        bounds.union(upserted.object.boundingBox);
      }
      stats.meshCount++;
    });
    if (meshesSpan !== null) {
      spans?.endSpan(meshesSpan as never);
    }

    if (look.instancing && batchInstanced) {
      const instancingSpan: unknown = spans?.startSpan('geometry.apply.instancing') ?? null;
      this.batchInstances(
        queue,
        graphics,
        (renderKey: string) => materials.collectionMaterial(rgbaOf(renderKey, frustum.meshes.materialIdByRenderKey)),
        (renderKey: string) => {
          const rgba: number[] | null = rgbaOf(renderKey, frustum.meshes.materialIdByRenderKey);
          return rgba === null || look.disableTransparentMaterials
            ? 1
            : Math.max(0, Math.min(1, rgba[3] * look.opacity));
        }
      );
      if (instancingSpan !== null) {
        spans?.endSpan(instancingSpan as never);
      }
    }

    const linesSpan: unknown = spans?.startSpan('geometry.apply.lines') ?? null;
    lines.forEach((line: HsDecodedLine, index: number) => {
      const renderKey: string = hsNormalizeGeometryId(
        line.geometryId ?? frustum.lines.renderKeys[index] ?? `line:${index}`
      );
      const upserted: HsSceneUpsertResult<HsGraphicLines> | null = this.objects.upsertLine(
        renderKey,
        line,
        `ingest-line-${index}`,
        materials.lineMaterial(rgbaOf(renderKey, frustum.lines.materialIdByRenderKey)),
        frustum.lines.worldMatricesByRenderKey[renderKey],
        queue,
        graphics
      );
      if (upserted !== null) {
        bounds.union(upserted.object.boundingBox);
        stats.lineCount++;
      }
    });
    if (linesSpan !== null) {
      spans?.endSpan(linesSpan as never);
    }

    const pointCloudsSpan: unknown = spans?.startSpan('geometry.apply.pointClouds') ?? null;
    pointClouds.forEach((pointCloud: HsDecodedPointCloud, index: number) => {
      const renderKey: string = hsNormalizeGeometryId(
        pointCloud.geometryId ?? frustum.pointClouds.renderKeys[index] ?? `point-cloud:${index}`
      );
      const upserted: (HsSceneUpsertResult<HsGraphicPoints> & { collidedPoints: number }) | null =
        this.objects.upsertPointCloud(
          renderKey,
          pointCloud,
          `ingest-point-cloud-${index}`,
          materials.points,
          frustum.pointClouds.worldMatricesByRenderKey[renderKey],
          { highlightCollisions: look.highlightCollisions, pointSize: look.pointSize },
          queue,
          graphics
        );
      if (upserted !== null) {
        stats.collidedPointsCount += upserted.collidedPoints;
        bounds.union(upserted.object.boundingBox);
        stats.pointCloudCount++;
      }
    });
    if (pointCloudsSpan !== null) {
      spans?.endSpan(pointCloudsSpan as never);
    }
    // Upserts put kept objects back into the queue and rebuilt batches lose their hidden instances; a visibility prune
    // leaves batches with instances of dropped keys.
    this.applyNodeVisibility(target, options.preserveGeometryForVisibility === true);
    return { stats, bounds, materials };
  }

  /**
   * Replaces composite meshes (materials with per-vertex color groups) by one mesh per color part. Splits are
   * cached per render key and redone when the mesh, groups or color change; invalid groups skip the mesh and
   * are reported through `materialErrors`.
   */
  public expandMaterialParts(meshes: readonly HsDecodedMesh[], disableTransparentMaterials: boolean): HsDecodedMesh[] {
    const materialIds: Record<string, string> = this.frustum.meshes.materialIdByRenderKey;
    const expanded: HsDecodedMesh[] = [];
    for (const mesh of meshes) {
      const groups: HsVertexColorGroupView[] | undefined = this.vertexGroupsById[materialIds[mesh.geometryId]];
      if (groups === undefined || groups.length === 0) {
        this.materialErrors.delete(mesh.geometryId);
        this.meshPartsByRenderKey.delete(mesh.geometryId);
        expanded.push(mesh);
        continue;
      }
      const rgba: number[] = this.materialRgbaById[materialIds[mesh.geometryId]];
      let cached: HsCachedMeshMaterialParts | undefined = this.meshPartsByRenderKey.get(mesh.geometryId);
      if (
        cached === undefined ||
        cached.vertices !== mesh.vertices ||
        cached.normals !== mesh.normals ||
        cached.indices !== mesh.indices ||
        cached.groups !== groups ||
        cached.rgba !== rgba
      ) {
        for (const part of cached?.parts ?? []) {
          this.meshPartRebuildKeys.add(part.mesh.geometryId);
          this.meshPartInfoByRenderKey.delete(part.mesh.geometryId);
        }
        let parts: HsCachedMeshMaterialParts['parts'];
        let shared: object | undefined;
        let errorMessage: string | undefined;
        try {
          const split: HsSharedMeshMaterialSplitResult = this.sharedMaterialSplits.split(mesh, rgba, groups);
          parts = split.parts;
          shared = split.shared;
          this.materialErrors.delete(mesh.geometryId);
        } catch (error: unknown) {
          if (!(error instanceof HsMeshMaterialValidationError)) {
            throw error;
          }
          parts = [];
          errorMessage = error.message;
          this.materialErrors.set(mesh.geometryId, error.message);
          this.options.onMaterialError?.(this.materialErrorSummary());
          this.options.onEvent?.('geometry.material-error', error.message);
          // eslint-disable-next-line no-console
          console.error(`Viewer mesh material: ${error.message}`);
        }
        cached = {
          vertices: mesh.vertices,
          normals: mesh.normals,
          indices: mesh.indices,
          groups,
          rgba,
          parts,
          shared,
          errorMessage,
        };
        this.meshPartsByRenderKey.set(mesh.geometryId, cached);
      }
      if (cached.errorMessage !== undefined) {
        this.materialErrors.set(mesh.geometryId, cached.errorMessage);
      }
      for (const part of cached.parts) {
        this.meshPartInfoByRenderKey.set(part.mesh.geometryId, { parentRenderKey: mesh.geometryId, rgba: part.rgba });
        if (part.rgba[3] > 0 || disableTransparentMaterials) {
          expanded.push(part.mesh);
        }
      }
    }
    return expanded;
  }

  /** Render keys with composite meshes replaced by the keys of their visible parts. */
  public expandMaterialPartKeys(renderKeys: readonly string[], disableTransparentMaterials: boolean): string[] {
    const expanded: string[] = [];
    for (const key of renderKeys) {
      const cached: HsCachedMeshMaterialParts | undefined = this.meshPartsByRenderKey.get(key);
      if (cached === undefined) {
        expanded.push(key);
        continue;
      }
      for (const part of cached.parts) {
        if (part.rgba[3] > 0 || disableTransparentMaterials) {
          expanded.push(part.mesh.geometryId);
        }
      }
    }
    return expanded;
  }

  /** "none", or how many meshes were skipped with the first reason. */
  public materialErrorSummary(): string {
    const message: string | undefined = this.materialErrors.values().next().value;
    return message === undefined ? 'none' : `Skipped ${this.materialErrors.size} mesh objects: ${message}`;
  }

  /** Removes every scene object and forgets the material parts; the caller drops viewer caches. */
  public clearObjects(queue: HsGraphicQueue, graphics: HsGraphics): void {
    const objects: Set<HsGraphicObject> = new Set<HsGraphicObject>([
      ...this.objects.meshes.values(),
      ...this.objects.lines.values(),
      ...this.objects.pointClouds.values(),
    ]);
    const referenceCounts: Map<HsGeometry, number> = this.objects.countGeometryReferences();
    objects.forEach((object: HsGraphicObject) => this.objects.dispose(queue, graphics, object, referenceCounts));
    this.objects.meshes.clear();
    this.objects.meshSources.clear();
    this.objects.lines.clear();
    this.objects.pointClouds.clear();
    this.meshPartInfoByRenderKey.clear();
    this.meshPartsByRenderKey.clear();
    this.meshPartRebuildKeys.clear();
  }

  /** Batches interchangeable drawn meshes into instanced collections and retires their source objects. */
  public batchInstances(
    queue: HsGraphicQueue,
    graphics: HsGraphics,
    resolveMaterial: (renderKey: string) => HsMaterial,
    resolveAlpha: (renderKey: string) => number
  ): void {
    this.objectHooks.refreshCulling();
    const sources: Set<HsGraphicObject> = new Set(
      [...this.objects.meshSources.values()].filter(
        (object: HsGraphicObject) => !object.isDeleted && !object.geometry.isDeleted
      )
    );
    const referenceCounts: Map<HsGeometry, number> = this.objects.countGeometryReferences();
    const meshes: HsFrustumSceneState['meshes'] = this.frustum.meshes;
    const groups: Map<string, string[]> = hsGroupInstancingCandidates(meshes.renderKeys, (renderKey: string) => {
      const object: HsGraphicObject | undefined = this.objects.meshes.get(renderKey);
      if (object === undefined || object.material instanceof HsTextureMaterial) {
        return null;
      }
      if (object.isDeleted && !sources.has(object)) {
        return null;
      }
      const geometryId: string | undefined = meshes.geometryIdByRenderKey[renderKey];
      if (geometryId === undefined) {
        return null;
      }
      const alpha: number = resolveAlpha(renderKey);
      if (alpha < 1) {
        return null;
      }
      const materialId: string = meshes.materialIdByRenderKey[renderKey] ?? '';
      const rgba: number[] | undefined = materialId.length > 0 ? this.materialRgbaById[materialId] : undefined;
      const worldMatrix: number[] | undefined = meshes.worldMatricesByRenderKey[renderKey];
      return {
        geometryId,
        materialKey: `${materialId}|${rgba?.join(',') ?? ''}|${alpha}`,
        worldMatrix: Array.isArray(worldMatrix) && worldMatrix.length === 16 ? worldMatrix : object.worldMatrix.values,
      };
    });
    const retired: Set<HsGraphicObject> = new Set<HsGraphicObject>();
    for (const group of groups.values()) {
      const first: HsGraphicObject | undefined = this.objects.meshes.get(group[0] ?? '');
      if (first === undefined || !(first.geometry instanceof HsIndexedGeometry)) {
        continue;
      }
      for (const renderKeys of hsSplitInstancingBatches(group, Math.floor(first.geometry.indexArrayLength / 3))) {
        if (renderKeys.length >= 2) {
          this.objects.createInstancedBatch(
            renderKeys,
            queue,
            graphics,
            resolveMaterial,
            (renderKey: string) => meshes.worldMatricesByRenderKey[renderKey],
            sources,
            retired,
            referenceCounts
          );
        }
      }
    }
    for (const object of retired) {
      queue.removeObject(object);
      if (!object.isDeleted) {
        this.objects.releaseGeometryReference(object, referenceCounts);
      }
      object.isDeleted = true;
    }
    this.objectHooks.invalidateOrder();
  }

  /** Downloads a geometry payload, through the IndexedDB cache when it is on. */
  public loadPayload(
    kind: HsGeometryPayloadKind,
    geometryIds: readonly string[],
    request: HsGeometryPayloadRequest = {}
  ): Promise<ArrayBuffer> {
    return this.cachedPayload(
      kind,
      geometryIds,
      () => this.options.api.getGeometryPayload(kind, geometryIds, request),
      request.format,
      request.canonicalGeometryIds ?? []
    );
  }

  /**
   * Returns the cached payload for the key or loads it with `loader` and caches it. A cache that fails to
   * write is turned off and reported through `onWarning`.
   */
  public async cachedPayload(
    kind: HsGeometryPayloadKind,
    geometryIds: readonly string[],
    loader: () => Promise<ArrayBuffer>,
    format?: string,
    canonicalGeometryIds: readonly string[] = []
  ): Promise<ArrayBuffer> {
    if (geometryIds.length === 0) {
      return new ArrayBuffer(0);
    }
    // Point clouds are never persisted: their decoded points stay in the tab's memory at most.
    if (!this.loadSettings.useCache || kind === 'pointClouds') {
      return loader();
    }
    const { sceneId, modelId }: { sceneId: string; modelId: string } = this.options.cacheScope();
    const formats: { meshes: string; pointClouds: string } = this.options.geometryFormats();
    const cacheKey: string = hsMakeGeometryPayloadKey({
      kind,
      geometryIds: [...geometryIds],
      sceneId,
      modelId,
      format: format ?? (kind === 'meshes' ? formats.meshes : 'binary'),
      protocolVersion: GEOMETRY_PROTOCOL_VERSION,
      canonicalGeometryIds: [...canonicalGeometryIds],
    });
    if (this.loadSettings.cacheMode === 'read-through') {
      const cached: ArrayBuffer | null = await this.payloadCache.read(cacheKey);
      if (cached !== null) {
        this.stats.cacheHits++;
        return cached;
      }
      this.stats.cacheMisses++;
    }
    const loaded: ArrayBuffer = await loader();
    try {
      await this.payloadCache.write(cacheKey, { kind, sceneId, modelId }, loaded);
    } catch {
      this.loadSettings.useCache = false;
      this.options.onWarning?.('IndexedDB cache is unavailable. Cache mode has been disabled.');
    }
    return loaded;
  }

  /**
   * Decodes mesh, line and point cloud payloads, in workers when they are on. A worker failure falls back
   * to the main thread; a canceled decode rejects (see `hsIsDecodeCanceled`).
   */
  public async decodePayloads(
    meshes: ArrayBuffer,
    lines: ArrayBuffer,
    pointClouds: ArrayBuffer
  ): Promise<HsDecodedPayloads> {
    const spans: HsSceneSourceSpans | null = this.options.spans?.() ?? null;
    const span: unknown = spans?.startSpan('geometry.decode') ?? null;
    const endSpan = (): void => {
      if (span !== null) {
        spans?.endSpan(span as never);
      }
    };
    const epoch: number = this.options.currentEpoch();
    const startedAt: number = performance.now();
    if (!this.loadSettings.useWorkers) {
      const result: HsDecodedPayloads = await this.decodeOnMainThread(meshes, lines, pointClouds);
      this.recordDecode('main-thread', performance.now() - startedAt);
      endSpan();
      return result;
    }
    try {
      this.decodePool.ensure(this.loadSettings.workerPoolSize);
      if (this.decodePool.size === 0) {
        throw new Error('No decode workers are available.');
      }
      const [decodedMeshes, decodedLines, decodedPointClouds]: [
        HsDecodedMesh[],
        HsDecodedLine[],
        HsDecodedPointCloud[]
      ] = await Promise.all([
        meshes.byteLength === 0 ? Promise.resolve([]) : this.decodePool.decode<HsDecodedMesh>('meshes', meshes, epoch),
        lines.byteLength === 0 ? Promise.resolve([]) : this.decodePool.decode<HsDecodedLine>('lines', lines, epoch),
        pointClouds.byteLength === 0
          ? Promise.resolve([])
          : this.decodePool.decode<HsDecodedPointCloud>('pointClouds', pointClouds, epoch),
      ]);
      this.stats.workerStatus = 'enabled';
      this.recordDecode('workers', performance.now() - startedAt);
      endSpan();
      return { meshes: decodedMeshes, lines: decodedLines, pointClouds: decodedPointClouds };
    } catch (error: unknown) {
      if (hsIsDecodeCanceled(error)) {
        this.stats.decodeCanceled++;
        endSpan();
        throw error;
      }
      this.stats.workerStatus = 'enabled-fallback';
      this.stats.decodeFallbacks++;
      const result: HsDecodedPayloads = await this.decodeOnMainThread(meshes, lines, pointClouds);
      this.recordDecode('fallback', performance.now() - startedAt);
      endSpan();
      return result;
    }
  }

  /** Decodes a mesh payload on the main thread (binary or Draco). */
  public decodeMeshes(payload: ArrayBuffer): Promise<HsDecodedMesh[]> {
    return hsIsCompressedGeometryPayload(payload)
      ? hsDecodeCompressedMeshes(payload)
      : Promise.resolve(this.binaryDecoder.decodeMeshesBinary(payload));
  }

  /** Decodes a point cloud payload on the main thread (binary or Draco), interleaved like the decode workers do. */
  public async decodePointClouds(payload: ArrayBuffer): Promise<HsDecodedPointCloud[]> {
    const pointClouds: HsDecodedPointCloud[] = hsIsCompressedGeometryPayload(payload)
      ? await hsDecodeCompressedPointClouds(payload)
      : this.binaryDecoder.decodePointCloudsBinary(payload);
    pointClouds.forEach((pointCloud: HsDecodedPointCloud) =>
      hsInterleavePointCloudOrder(
        pointCloud.points as HsMutableNumericArray,
        pointCloud.colors as HsMutableNumericArray,
        pointCloud.collisions as HsMutableNumericArray
      )
    );
    return pointClouds;
  }

  /**
   * Geometry ids of meshes drawn with a composite (per-vertex color) material. Their payloads must keep the
   * canonical vertex order the color ranges refer to.
   */
  public compositeGeometryIds(): Set<string> {
    const meshes: HsFrustumGeometrySet = this.frustum.meshes;
    const ids: Set<string> = new Set();
    for (const renderKey of meshes.renderKeys) {
      const geometryId: string | undefined = meshes.geometryIdByRenderKey[renderKey];
      if (this.hasVertexGroups(renderKey) && geometryId !== undefined) {
        ids.add(geometryId);
      }
    }
    return ids;
  }

  /**
   * Of `geometryIds`, the meshes to download: not decoded yet, or composite meshes decoded without their
   * canonical vertex order. A mesh only composite instances draw needs no download once their corrected
   * originals are known.
   */
  public missingMeshGeometryIds(geometryIds: readonly string[]): string[] {
    const meshes: HsFrustumGeometrySet = this.frustum.meshes;
    const compositeIds: Set<string> = this.compositeGeometryIds();
    const fallbackIds: Set<string> = new Set();
    const requiredIds: Set<string> = new Set();
    for (const renderKey of meshes.renderKeys) {
      const id: string = meshes.geometryIdByRenderKey[renderKey];
      if (this.hasVertexGroups(renderKey) && this.geometry.corrected.has(sourceKeyOf(renderKey, id))) {
        fallbackIds.add(id);
      } else {
        requiredIds.add(id);
      }
    }
    return geometryIds.filter((id: string) => {
      if (fallbackIds.has(id) && !requiredIds.has(id)) {
        return false;
      }
      const mesh: HsDecodedMesh | undefined = this.geometry.meshes.get(id);
      return mesh === undefined || (compositeIds.has(id) && mesh.canonicalVertexOrder !== true);
    });
  }

  /** Of `geometryIds`, the lines or point clouds not decoded yet. */
  public missingGeometryIds(kind: 'lines' | 'pointClouds', geometryIds: readonly string[]): string[] {
    const decoded: Map<string, unknown> = kind === 'lines' ? this.geometry.lines : this.geometry.pointClouds;
    return geometryIds.filter((id: string) => !decoded.has(id));
  }

  /** Of `geometryIds`, the meshes composite instances without a corrected original need in canonical order. */
  public canonicalMeshGeometryIds(geometryIds: readonly string[]): string[] {
    const meshes: HsFrustumGeometrySet = this.frustum.meshes;
    const requested: Set<string> = new Set(geometryIds);
    const ids: Set<string> = new Set();
    for (const renderKey of meshes.renderKeys) {
      const geometryId: string = meshes.geometryIdByRenderKey[renderKey];
      if (
        requested.has(geometryId) &&
        this.hasVertexGroups(renderKey) &&
        !this.geometry.corrected.has(sourceKeyOf(renderKey, geometryId))
      ) {
        ids.add(geometryId);
      }
    }
    return Array.from(ids);
  }

  /**
   * The decoded meshes of `renderKeys`, renamed to their render keys. Composite instances use their
   * corrected original and are left out until a canonically ordered mesh is available.
   */
  public collectMeshes(renderKeys: readonly string[]): HsDecodedMesh[] {
    const geometryIdByRenderKey: Record<string, string> = this.frustum.meshes.geometryIdByRenderKey;
    const collected: HsDecodedMesh[] = [];
    for (const renderKey of renderKeys) {
      const geometryId: string | undefined = geometryIdByRenderKey[renderKey];
      if (geometryId === undefined) {
        continue;
      }
      const composite: boolean = this.hasVertexGroups(renderKey);
      const decoded: HsDecodedMesh | undefined =
        (composite ? this.geometry.corrected.get(sourceKeyOf(renderKey, geometryId)) : undefined) ??
        this.geometry.meshes.get(geometryId);
      if (decoded !== undefined && (!composite || decoded.canonicalVertexOrder === true)) {
        collected.push({ ...decoded, geometryId: renderKey });
      }
    }
    return collected;
  }

  /** The decoded lines of `renderKeys`, renamed to their render keys. */
  public collectLines(renderKeys: readonly string[]): HsDecodedLine[] {
    return collectByRenderKeys(renderKeys, this.frustum.lines.geometryIdByRenderKey, this.geometry.lines);
  }

  /** The decoded point clouds of `renderKeys`, renamed to their render keys. */
  public collectPointClouds(renderKeys: readonly string[]): HsDecodedPointCloud[] {
    return collectByRenderKeys(renderKeys, this.frustum.pointClouds.geometryIdByRenderKey, this.geometry.pointClouds);
  }

  /**
   * Looks up the original meshes of composite instances not checked yet, downloads the missing ones in
   * canonical vertex order and keeps them corrected into each instance's space. Stops when the scene changes.
   */
  public async refreshCompositeSources(): Promise<void> {
    const epoch: number = this.options.currentEpoch();
    const pending: Map<string, string> = new Map();
    for (const renderKey of this.frustum.meshes.renderKeys) {
      if (!this.hasVertexGroups(renderKey)) {
        continue;
      }
      const [nodeId, geometryId]: string[] = renderKey.split('|');
      const sourceKey: string = `${nodeId}|${geometryId}`;
      if (!this.geometry.checkedOriginalSources.has(sourceKey)) {
        pending.set(sourceKey, nodeId);
      }
    }
    if (pending.size === 0) {
      return;
    }
    const sources: HsOriginalMeshSource[] = [];
    for (const batch of chunk(Array.from(new Set(pending.values())), ORIGINAL_SOURCE_REQUEST_MAX_SIZE)) {
      sources.push(...(await this.options.api.getOriginalMeshSources(batch)));
      if (epoch !== this.options.currentEpoch()) {
        return;
      }
    }
    const missingIds: string[] = Array.from(
      new Set(sources.map((source: HsOriginalMeshSource) => source.originalGeometryId))
    ).filter((id: string) => !this.geometry.originals.has(id));
    for (const batch of chunk(missingIds, this.options.downloadBatchSize())) {
      const payload: ArrayBuffer = await this.loadPayload('meshes', batch, {
        format: this.options.geometryFormats().meshes,
        canonicalGeometryIds: batch,
      });
      const meshes: HsDecodedMesh[] = await this.decodeMeshes(payload);
      if (epoch !== this.options.currentEpoch()) {
        return;
      }
      for (const mesh of meshes) {
        this.geometry.originals.set(mesh.geometryId, mesh);
      }
    }
    for (const source of sources) {
      const sourceKey: string = `${source.nodeId}|${source.geometryId}`;
      if (!pending.has(sourceKey)) {
        throw new Error(`Unexpected original mesh source for ${sourceKey}.`);
      }
      const original: HsDecodedMesh | undefined = this.geometry.originals.get(source.originalGeometryId);
      if (original === undefined) {
        throw new Error(`Original mesh ${source.originalGeometryId} was not returned by the API.`);
      }
      this.geometry.corrected.set(sourceKey, hsCorrectOriginalMesh(original, source));
    }
    for (const sourceKey of pending.keys()) {
      this.geometry.checkedOriginalSources.add(sourceKey);
    }
    this.trimMeshCache();
  }

  /** Forgets composite originals and material errors, for example when the scene changes. */
  public clearCompositeSources(): void {
    this.materialErrors.clear();
    this.geometry.clearOriginals();
  }

  /** Evicts decoded meshes the current Frustum does not use while the cache is over budget. */
  public trimMeshCache(): void {
    const meshes: HsFrustumGeometrySet = this.frustum.meshes;
    const activeSourceKeys: Set<string> = new Set(
      meshes.renderKeys.map((renderKey: string) => {
        const [nodeId, geometryId]: string[] = renderKey.split('|');
        return `${nodeId}|${geometryId}`;
      })
    );
    if (
      this.geometry.trimInactiveMeshes(this.options.meshCacheBudgetBytes, new Set(meshes.geometryIds), activeSourceKeys)
    ) {
      this.options.onCacheChanged?.();
    }
  }

  /** Texture ids the current Frustum meshes need, from their instances or their materials. */
  public textureIds(): string[] {
    const meshes: HsFrustumSceneState['meshes'] = this.frustum.meshes;
    return Array.from(
      new Set(
        meshes.renderKeys
          .map(
            (key: string) =>
              meshes.textureIdByRenderKey[key] ?? this.textureIdByMaterialId[meshes.materialIdByRenderKey[key]]
          )
          .filter((id: string | null | undefined): id is string => id !== null && id !== undefined && id.length > 0)
      )
    );
  }

  /** Downloads and uploads the textures not loaded yet, in batches; `onBatchLoaded` runs after each batch. */
  public async loadTextures(textureIds: readonly string[], onBatchLoaded?: () => void): Promise<void> {
    const missing: string[] = Array.from(new Set(textureIds)).filter((id: string) => !this.textures.has(id));
    const batchSize: number = Math.min(this.options.textureBatchSize(), TEXTURE_REQUEST_MAX_SIZE);
    for (let start: number = 0; start < missing.length; start += batchSize) {
      const batch: string[] = missing.slice(start, start + batchSize);
      if (batch.length === 1) {
        await this.loadTexture(batch[0]);
        onBatchLoaded?.();
        continue;
      }
      const epoch: number = this.options.currentEpoch();
      const blobs: Map<string, Blob> = await this.options.api.getDocuments(batch);
      if (epoch !== this.options.currentEpoch()) {
        return;
      }
      await Promise.all(
        batch.map((id: string) => {
          const blob: Blob | undefined = blobs.get(id);
          if (blob === undefined) {
            throw new Error(`Texture batch did not return ${id}.`);
          }
          return this.loadTexture(id, blob);
        })
      );
      onBatchLoaded?.();
    }
  }

  /** Downloads (unless given) and uploads one texture with mipmaps; evicts unused textures over budget first. */
  public async loadTexture(textureId: string, preloadedBlob?: Blob): Promise<void> {
    if (this.textures.has(textureId) || this.options.gl() === null) {
      return;
    }
    const epoch: number = this.options.currentEpoch();
    const blob: Blob = preloadedBlob ?? (await this.options.api.getDocument(textureId));
    const bitmap: ImageBitmap = await createImageBitmap(blob);
    try {
      const gl: WebGL2RenderingContext | null = this.options.gl();
      if (epoch !== this.options.currentEpoch() || gl === null || this.textures.has(textureId)) {
        return;
      }
      const estimatedBytes: number = Math.ceil((bitmap.width * bitmap.height * 4 * 4) / 3);
      this.trimTextures(estimatedBytes);
      const texture: WebGLTexture | null = gl.createTexture();
      if (texture === null) {
        throw new Error(`Unable to create texture ${textureId}.`);
      }
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, bitmap);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.generateMipmap(gl.TEXTURE_2D);
      gl.bindTexture(gl.TEXTURE_2D, null);
      this.textures.set(textureId, texture);
      this.textureBytes.set(textureId, estimatedBytes);
      this.options.onCacheChanged?.();
    } finally {
      bitmap.close();
    }
  }

  /** Deletes textures the view does not use until `incomingBytes` more fit in the budget. */
  public trimTextures(incomingBytes: number = 0): void {
    let total: number = 0;
    for (const size of this.textureBytes.values()) {
      total += size;
    }
    const gl: WebGL2RenderingContext | null = this.options.gl();
    if (total + incomingBytes <= this.options.textureBudgetBytes || gl === null) {
      return;
    }
    const activeIds: Set<string> = new Set([...this.textureIds(), ...this.renderedTextureIds]);
    for (const [id, texture] of this.textures) {
      if (total + incomingBytes <= this.options.textureBudgetBytes) {
        break;
      }
      if (!activeIds.has(id)) {
        gl.deleteTexture(texture);
        this.textures.delete(id);
        total -= this.textureBytes.get(id) ?? 0;
        this.textureBytes.delete(id);
      }
    }
    this.options.onCacheChanged?.();
  }

  public clearTextures(): void {
    const gl: WebGL2RenderingContext | null = this.options.gl();
    if (gl !== null) {
      for (const texture of this.textures.values()) {
        gl.deleteTexture(texture);
      }
    }
    this.textures.clear();
    this.textureBytes.clear();
    this.renderedTextureIds.clear();
    this.textureIdByMaterialId = {};
  }

  /**
   * Loads the colors, textures and vertex color groups of the materials. Ids the materials endpoint does not
   * know are looked up in the original materials of the scene's root node trees (all roots when none given).
   */
  public async refreshMaterials(
    sceneId: string,
    rootNodeIds: readonly string[] = [],
    materialIds: readonly string[] = [],
    loadTextures: boolean = true
  ): Promise<void> {
    const epoch: number = this.options.currentEpoch();
    let roots: string[] = Array.from(
      new Set(rootNodeIds.map((id: string) => hsNormalizeOptionalId(id)).filter((id: string) => id !== 'none'))
    );
    if (roots.length === 0) {
      const sceneRoots: HsNodeSmallView[] = await this.options.api.getSceneRootNodes(sceneId).catch(() => []);
      roots = sceneRoots.map((root: HsNodeSmallView) => root.id);
    }
    const ids: string[] = Array.from(
      new Set(materialIds.map((id: string) => hsNormalizeOptionalId(id)).filter((id: string) => id !== 'none'))
    );
    const byId: HsMaterialView[] = await this.options.api.getMaterials(ids).catch(() => []);
    if (epoch !== this.options.currentEpoch()) {
      return;
    }
    const rgbaById: Record<string, number[]> = { ...this.materialRgbaById };
    const apply = (material: unknown): void => {
      const materialId: string = hsMaterialId(material);
      if (materialId.length === 0) {
        return;
      }
      rgbaById[materialId] = hsMaterialRgba(material);
      const textureId: string | null | undefined = (material as HsMaterialView).textureId;
      if (textureId !== null && textureId !== undefined && textureId.length > 0) {
        this.textureIdByMaterialId[materialId] = textureId;
      }
      const groups: unknown = (material as HsMaterialView).vertexColorGroups;
      if (Array.isArray(groups)) {
        this.vertexGroupsById[materialId] = (material as HsMaterialView).vertexColorGroups ?? [];
      }
    };
    byId.forEach((material: HsMaterialView) => apply(material));
    if (ids.length === 0 || ids.some((id: string) => rgbaById[id] === undefined)) {
      const trees: HsMaterialView[][] = await Promise.all(
        roots.map((root: string) => this.options.api.getNodeTreeOriginalMaterials(root).catch(() => []))
      );
      if (epoch !== this.options.currentEpoch()) {
        return;
      }
      for (const materials of trees) {
        for (const material of materials) {
          const materialId: string = hsMaterialId(material);
          if (materialId.length > 0 && rgbaById[materialId] === undefined) {
            apply(material);
          }
        }
      }
    }
    this.materialRgbaById = rgbaById;
    if (loadTextures) {
      await this.loadTextures(this.textureIds());
    }
  }

  /** Loads the materials of the current Frustum instances that are not loaded yet, then their textures. */
  public async refreshMissingMaterials(
    sceneId: string,
    rootNodeIds: readonly string[],
    loadTextures: boolean = true
  ): Promise<void> {
    const materialIds: string[] = Array.from(
      new Set([
        ...Object.values(this.frustum.meshes.materialIdByRenderKey),
        ...Object.values(this.frustum.lines.materialIdByRenderKey),
        ...Object.values(this.frustum.pointClouds.materialIdByRenderKey),
      ])
    ).filter((id: string) => id.length > 0);
    const missing: string[] = materialIds.filter((id: string) => this.materialRgbaById[id] === undefined);
    if (missing.length === 0) {
      if (loadTextures) {
        await this.loadTextures(this.textureIds());
      }
      return;
    }
    await this.refreshMaterials(sceneId, rootNodeIds, missing, loadTextures);
  }

  /**
   * Draws decoded geometry through the host in `renderBatchSize` chunks, yielding a frame whenever the
   * draw takes longer than `renderBudgetMs`. Only the first chunk prunes stale objects.
   */
  private async applyInChunks(
    host: HsSceneRefreshHost,
    meshes: readonly HsDecodedMesh[],
    lines: readonly HsDecodedLine[],
    pointClouds: readonly HsDecodedPointCloud[],
    preserveGeometryForVisibility: boolean,
    deferStaleObjectPruning: boolean
  ): Promise<HsSceneApplyStats> {
    const epoch: number = this.options.currentEpoch();
    const frustum: HsFrustumSceneState = this.frustum;
    const options: HsSceneRefreshApplyOptions = {
      expectedMeshGeometryIds: frustum.meshes.renderKeys,
      expectedLineGeometryIds: frustum.lines.renderKeys,
      expectedPointCloudGeometryIds: frustum.pointClouds.renderKeys,
      stage: 'chunk',
      preserveGeometryForVisibility,
      batchInstancedGeometry: false,
      skipStaleObjectPruning: deferStaleObjectPruning,
    };
    const size: number = host.settings.renderBatchSize;
    const draws: (readonly [HsFrustumDownloadKind, (drawOptions: HsSceneRefreshApplyOptions) => HsSceneApplyStats])[] =
      [
        ...chunk(meshes, size).map(
          (batch: HsDecodedMesh[]) =>
            ['meshes', (drawOptions: HsSceneRefreshApplyOptions) => host.apply(batch, [], [], drawOptions)] as const
        ),
        ...chunk(lines, size).map(
          (batch: HsDecodedLine[]) =>
            ['lines', (drawOptions: HsSceneRefreshApplyOptions) => host.apply([], batch, [], drawOptions)] as const
        ),
        ...chunk(pointClouds, size).map(
          (batch: HsDecodedPointCloud[]) =>
            [
              'pointClouds',
              (drawOptions: HsSceneRefreshApplyOptions) => host.apply([], [], batch, drawOptions),
            ] as const
        ),
      ];
    const stats: HsSceneApplyStats = hsEmptySceneApplyStats();
    let startedAt: number = performance.now();
    let first: boolean = true;
    for (const [kind, draw] of draws) {
      if (epoch !== this.options.currentEpoch()) {
        throw new Error(HS_GEOMETRY_DECODE_CANCELED);
      }
      const drawOptions: HsSceneRefreshApplyOptions = first ? options : { ...options, skipStaleObjectPruning: true };
      first = false;
      addStats(
        stats,
        this.timedSync(`geometry.apply.chunk.${kind}`, () => draw(drawOptions))
      );
      if (performance.now() - startedAt >= host.settings.renderBudgetMs) {
        await host.nextFrame();
        startedAt = performance.now();
      }
    }
    if (epoch !== this.options.currentEpoch()) {
      throw new Error(HS_GEOMETRY_DECODE_CANCELED);
    }
    return stats;
  }

  private downloadPayload(
    kind: HsFrustumDownloadKind,
    geometryIds: readonly string[],
    signal: AbortSignal
  ): Promise<ArrayBuffer> {
    if (kind === 'lines') {
      return this.loadPayload('lines', geometryIds, { signal });
    }
    const formats: { meshes: string; pointClouds: string } = this.options.geometryFormats();
    return kind === 'meshes'
      ? this.loadPayload('meshes', geometryIds, {
          signal,
          format: formats.meshes,
          canonicalGeometryIds: this.canonicalMeshGeometryIds(geometryIds),
        })
      : this.loadPayload('pointClouds', geometryIds, { signal, format: formats.pointClouds });
  }

  private async timed<T>(name: string, work: () => Promise<T>): Promise<T> {
    const spans: HsSceneSourceSpans | null = this.options.spans?.() ?? null;
    const span: unknown = spans?.startSpan(name) ?? null;
    try {
      return await work();
    } finally {
      if (span !== null) {
        spans?.endSpan(span as never);
      }
    }
  }

  private timedSync<T>(name: string, work: () => T): T {
    const spans: HsSceneSourceSpans | null = this.options.spans?.() ?? null;
    const span: unknown = spans?.startSpan(name) ?? null;
    try {
      return work();
    } finally {
      if (span !== null) {
        spans?.endSpan(span as never);
      }
    }
  }

  /** Deletes uploaded textures and compiled material programs (scene objects go with `close`). */
  private releaseGpuResources(): void {
    this.clearTextures();
    this.materials.clear(this.options.gl());
    this.lastLookSignature = null;
  }

  /**
   * Hides and shows the drawn objects as `nodeVisibility` says, and masks batch instances a visibility prune dropped;
   * free when nothing is or was hidden and nothing was pruned.
   */
  private applyNodeVisibility(target?: Pick<HsSceneTarget, 'graphics' | 'queue'>, pruned: boolean = false): void {
    const viewer: HsSceneViewer | null = this.viewer;
    const resolved: Pick<HsSceneTarget, 'graphics' | 'queue'> | null =
      target ?? (viewer === null ? null : { graphics: viewer.surface.graphics, queue: viewer.surface.objectsQueue });
    if (resolved === null || (!pruned && this.nodeVisibility.isEmpty && !this.objects.hasHidden)) {
      return;
    }
    const visibility: HsNodeVisibility = this.nodeVisibility;
    if (
      this.objects.applyHidden(
        (renderKey: string): boolean => visibility.isHidden(hsFrustumRenderKeyNodeId(renderKey)),
        resolved.queue,
        resolved.graphics
      )
    ) {
      viewer?.invalidate();
    }
  }

  /** Draws through `applyGeometry` into the installing viewer and reports progress to listeners. */
  private viewerHost(viewer: HsSceneViewer, visibilityOnly: boolean): HsSceneRefreshHost {
    this.abortController?.abort();
    const controller: AbortController = new AbortController();
    this.abortController = controller;
    const look: HsSceneLook = this.options.look?.() ?? HS_DEFAULT_SCENE_LOOK;
    const settings: HsSceneStreamingSettings = this.streaming;
    // The request describes this camera; only a later move needs another refresh.
    this.snapshotCamera(viewer);
    const target: HsSceneTarget = {
      gl: viewer.gl,
      graphics: viewer.surface.graphics,
      queue: viewer.surface.objectsQueue,
    };
    const apply = (
      meshes: readonly HsDecodedMesh[],
      lines: readonly HsDecodedLine[],
      pointClouds: readonly HsDecodedPointCloud[],
      options: HsSceneApplyOptions,
      stage: HsSceneAppliedEvent['stage']
    ): HsSceneApplyStats => {
      const stats: HsSceneApplyStats = this.applyGeometry(meshes, lines, pointClouds, target, look, options).stats;
      viewer.invalidate();
      const event: HsSceneAppliedEvent = { stats, stage };
      this.listeners.applied.forEach((listener: (value: HsSceneAppliedEvent) => void) => listener(event));
      return stats;
    };
    return {
      frustumRequest: (sessionId: string): HsFrustumRequestInfo =>
        hsMakeFrustumRequest(
          {
            worldMatrix: viewer.camera.worldMatrix.values,
            projectionMatrix: viewer.camera.projectionMatrix.values,
            projectionMatrixInverse: viewer.camera.projectionMatrixInverse.values,
            viewportHeightPixels: viewer.gl.drawingBufferHeight,
          },
          settings.frustumObjects,
          sessionId,
          settings.nodeTags,
          settings.cutBox ?? null,
          this.viewSessionId ?? undefined
        ),
      apply: (
        meshes: readonly HsDecodedMesh[],
        lines: readonly HsDecodedLine[],
        pointClouds: readonly HsDecodedPointCloud[],
        options: HsSceneRefreshApplyOptions
      ): HsSceneApplyStats => apply(meshes, lines, pointClouds, options, options.stage),
      nextFrame:
        this.options.nextFrame ??
        ((): Promise<void> =>
          new Promise<void>((resolve: () => void) => {
            // Workers and tests have no animation frames; a short timeout yields as well.
            if (typeof globalThis.requestAnimationFrame === 'function') {
              globalThis.requestAnimationFrame(() => resolve());
            } else {
              globalThis.setTimeout(resolve, 16);
            }
          })),
      settings,
      lookSignature: look.signature,
      signal: controller.signal,
      rootNodeIds: this.rootNodeIds,
      visibilityOnly,
      superseded: (): boolean => this.scheduler.pending,
      onMeshReplacement: (): void => viewer.render.clearMeshBatches(),
      onTexturesLoaded: (): void => {
        if (!this.textureIds().some((textureId: string) => this.textures.has(textureId))) {
          return;
        }
        // New textures change materials; rebuild them for what is drawn. Texture loading races geometry decoding
        // during LOD replacement, so nothing is pruned: the final draw commits the complete replacement.
        viewer.render.clearMeshBatches();
        this.materials.clear(viewer.gl);
        apply(
          this.drawnMeshes,
          this.drawnLines,
          this.drawnPointClouds,
          {
            expectedMeshGeometryIds: this.frustum.meshes.renderKeys,
            expectedLineGeometryIds: this.frustum.lines.renderKeys,
            expectedPointCloudGeometryIds: this.frustum.pointClouds.renderKeys,
            preserveGeometryForVisibility: visibilityOnly,
            batchInstancedGeometry: settings.instancing,
            skipStaleObjectPruning: true,
          },
          'textures'
        );
      },
      onProgress: (progress: HsSceneRefreshProgress): void =>
        this.listeners.progress.forEach((listener: (value: HsSceneRefreshProgress) => void) => listener(progress)),
    };
  }

  /** Refreshes once the camera has been still for `refreshDelayMs`; moves during a refresh supersede it. */
  private followCamera(viewer: HsSceneViewer): void {
    if (!this.snapshotCamera(viewer) || this.sceneId === null) {
      return;
    }
    this.scheduleUpdate();
  }

  /** Copies the camera matrix; returns whether it changed since the last copy. */
  private snapshotCamera(viewer: HsSceneViewer): boolean {
    const matrix: ArrayLike<number> = viewer.camera.worldMatrix.values;
    let moved: boolean = false;
    for (let index: number = 0; index < 16; index++) {
      // Written so that the initial NaN entries count as moved.
      if (!(Math.abs(this.lastCameraMatrix[index] - matrix[index]) <= 1e-6)) {
        moved = true;
        this.lastCameraMatrix[index] = matrix[index];
      }
    }
    return moved;
  }

  /** (Re)starts the quiet-time timer; while a refresh runs it marks that refresh superseded instead. */
  private scheduleUpdate(): void {
    this.scheduler.schedule(
      this.streaming.refreshDelayMs,
      () => this.options.canRefresh?.() === false,
      () => void this.update()
    );
  }

  private hasVertexGroups(renderKey: string): boolean {
    const groups: HsVertexColorGroupView[] | undefined =
      this.vertexGroupsById[this.frustum.meshes.materialIdByRenderKey[renderKey]];
    return groups !== undefined && groups.length > 0;
  }

  private async decodeOnMainThread(
    meshes: ArrayBuffer,
    lines: ArrayBuffer,
    pointClouds: ArrayBuffer
  ): Promise<HsDecodedPayloads> {
    const [decodedMeshes, decodedLines, decodedPointClouds]: [HsDecodedMesh[], HsDecodedLine[], HsDecodedPointCloud[]] =
      await Promise.all([
        this.decodeMeshes(meshes),
        Promise.resolve(this.binaryDecoder.decodeLinesBinary(lines)),
        this.decodePointClouds(pointClouds),
      ]);
    return { meshes: decodedMeshes, lines: decodedLines, pointClouds: decodedPointClouds };
  }

  private recordDecode(mode: string, elapsedMs: number): void {
    this.stats.decodeLastMode = mode;
    this.stats.decodeLastMs = elapsedMs;
    this.stats.decodeSamples++;
    const samples: number = this.stats.decodeSamples;
    this.stats.decodeAvgMs = (this.stats.decodeAvgMs * (samples - 1) + elapsedMs) / Math.max(1, samples);
  }
}

/** The material's id in lower case, or an empty string. */
export function hsMaterialId(material: unknown): string {
  if (material === null || typeof material !== 'object') {
    return '';
  }
  const rawId: unknown = (material as { id?: unknown; Id?: unknown }).id ?? (material as { Id?: unknown }).Id;
  return typeof rawId === 'string' ? hsNormalizeGeometryId(rawId) : '';
}

/** The material's RGBA in 0..1; missing or non-finite channels fall back to light grey and opaque. */
export function hsMaterialRgba(material: unknown): number[] {
  if (material === null || typeof material !== 'object') {
    return [...DEFAULT_MATERIAL_RGBA];
  }
  const value: unknown = (material as { rgba?: unknown }).rgba ?? (material as { Rgba?: unknown }).Rgba;
  if (!Array.isArray(value) || value.length < 3) {
    return [...DEFAULT_MATERIAL_RGBA];
  }
  const channel = (index: number, fallback: number): number => {
    const item: unknown = value[index];
    return typeof item === 'number' && Number.isFinite(item) ? item : fallback;
  };
  return [channel(0, 0.76), channel(1, 0.76), channel(2, 0.76), channel(3, 1)];
}

/** Key of a composite instance's corrected original: its node and geometry. */
function sourceKeyOf(renderKey: string, geometryId: string): string {
  return `${renderKey.split('|')[0]}|${geometryId}`;
}

function collectByRenderKeys<T extends { geometryId: string }>(
  renderKeys: readonly string[],
  geometryIdByRenderKey: Record<string, string>,
  decodedById: ReadonlyMap<string, T>
): T[] {
  const collected: T[] = [];
  for (const renderKey of renderKeys) {
    const geometryId: string | undefined = geometryIdByRenderKey[renderKey];
    const decoded: T | undefined = geometryId === undefined ? undefined : decodedById.get(geometryId);
    if (decoded !== undefined) {
      collected.push({ ...decoded, geometryId: renderKey });
    }
  }
  return collected;
}

function chunk<T>(items: readonly T[], size: number): T[][] {
  const chunks: T[][] = [];
  const step: number = Math.max(1, Math.floor(size));
  for (let index: number = 0; index < items.length; index += step) {
    chunks.push(items.slice(index, index + step));
  }
  return chunks;
}

function addStats(target: HsSceneApplyStats, source: HsSceneApplyStats): void {
  for (const key of Object.keys(target) as (keyof HsSceneApplyStats)[]) {
    target[key] += source[key];
  }
}

function newSessionId(): string {
  const crypto: Crypto | undefined = globalThis.crypto;
  if (crypto?.randomUUID !== undefined) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}

/** The node id of a render key, or null for instances without a known node. */
export function hsNodeIdOfRenderKey(renderKey: string): string | null {
  const nodeId: string = renderKey.split('|')[0] ?? '';
  return nodeId.length > 0 && nodeId !== 'node:unknown' ? nodeId : null;
}

/** Copies plane values between storages with as many planes; the target's shaders read them on the next draw. */
function copyClippingPlanes(source: HsClippingStorage, target: HsClippingStorage): void {
  const count: number = Math.min(source.clippingPlanes.length, target.clippingPlanes.length);
  for (let index: number = 0; index < count; index++) {
    target.clippingPlanes[index].set(source.clippingPlanes[index].normal, source.clippingPlanes[index].offset);
  }
}
